import { reportBelongsToSite, siteForReport } from '../../lib/reportSiteIdentity';
/**
 * DocumentLibrary — per-report document viewer with category folders.
 * Users can view, rename, delete documents, and upload new ones.
 */
import { orderEmailAttachments } from '../../lib/emailDocumentGroups';
import React, { useState, useMemo, useCallback } from 'react';
import { useStore } from '../../store';
import type { SiteDocument } from '../../types/siteRag';
// useStore used for fresh state reads during upload
import { FolderOpen, FileText, FileSpreadsheet, FileImage, FileVideo, FileCode, FileType, File as FileGeneric, Trash2, Upload, X, ChevronRight, ChevronDown, Pencil, Check, Download, Plus, RefreshCw, LayoutGrid, List as ListIcon, UploadCloud, Info, Search, SearchX, Sparkles } from 'lucide-react';
import { EmptyState } from '../ui/aurora';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { isLibraryIndexDoc, refreshLibraryIndex } from '../../services/knowledge/libraryIndex';
// UXS-7 — `usedInReportTypes` (the legacy intent-stamp "Cited by"
// source) is no longer imported here. The Cited-by row now reads from
// `bodyCitedInReportTypes` (CSE-derived `report.claims[]` ground truth).
// `applicableReportTypes` remains for the filter pre-resolver (the
// classifier-driven semantic is still useful for filtering the doc list,
// just not for the per-row badge — which UXS-7 removed entirely).
import { applicableReportTypes, bodyCitedInReportTypes } from '../../lib/documentUsage';
import { isAiOnlyEvidenceMode } from '../../lib/evidenceReadiness';
import { createSiteDocumentBodyLoader } from '../../lib/documentBody';
import { createSiteDocumentOriginalLoader } from '../../lib/siteDocumentOriginalClient';
import { siteOwnerUid } from '../../lib/siteOwnerPath';
import { auth } from '../../firebase';
import { siteDocumentMetadataBasisHash } from '../../lib/siteDocumentMetadata';
import { EvidenceModeControl } from './EvidenceModeControl';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { getDocumentChecklistAcross } from '../../lib/documentChecklist';
import { docMatchesPatterns } from '../../config/documentChecklists';
import { useUrlSearchParamSync } from '../../lib/useUrlSearchParamSync';
import { categorize } from '../../lib/documentCategory';
import { isNoisyDoc } from '../../lib/docNoiseFilter';
// UFL — shared upload validator. Replaces the inline 10 MB / VALID_EXTS
// gate that collapsed four rejection reasons into one misleading toast.
import {
  UPLOAD_MAX_FILE_BYTES,
  classifyUploadFileList,
  describeUploadRejection,
  summarizeRejections,
} from '../../lib/uploadValidation';
// UFL-5 — one telemetry event per rejected upload so admin can roll
// up "Upload friction" by reason / surface and decide whether to lift
// the 100 MB cap further.
import { emitUploadRejected, emitAuditEvent } from '../../lib/auditLogClient';
import { displayFileName, breadcrumbString } from '../../lib/docFileNameDisplay';
import { DocViewerDialog } from './DocViewerDialog';
import { DocDetailDrawer } from './DocDetailDrawer';
// DAT-1/2/3 — doc-first applicability transparency surfaces.
import { DocApplicabilityChips } from './DocApplicabilityChips';
import { DocLineageInspector } from './DocLineageInspector';
import {
  rankDocsByImportanceMap,
  type RankedDoc,
} from '../../lib/docImportance';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { MoveFolderDialog } from './MoveFolderDialog';
// UPE-2 — per-doc extraction status chip on the doc row.
import { DocRowStatusChip } from './docChecklist/DocRowStatusChip';
import { DocRowTruncationChip } from './docChecklist/DocRowTruncationChip';
import { DocumentsUploadLandingCard } from './DocumentsUploadLandingCard';
// DLB-1 + DLB-2 — bulk-delete affordances. `pickNewestRevision` keeps
// the "delete older revisions" candidate set consistent with whichever
// row the GROUP REVISIONS toggle picks as the visible canonical.
import { openConfirmDialog } from '../ui/confirmDialog';
import { pickNewestRevision } from '../../lib/revisionOrdering';
// DDL-FOLLOWUP-CONFIRM-DIALOG + DDL-FOLLOWUP-RESTORE — soft-delete helpers.
import {
  SOFT_DELETE_TTL_DAYS,
  isLiveDoc,
  countReferencingReports,
} from '../../lib/documentSoftDelete';

/**
 * Founder QA r3 item 5e — custom dataTransfer type for dragging a
 * document CARD onto a folder tab. The custom MIME keeps card drags
 * distinguishable from OS file drags everywhere that matters:
 *   - the window-level `isDraggingFiles` listener checks for 'Files',
 *     so a card drag never expands the upload dropzone;
 *   - the folder tabs only accept drags carrying THIS type, so an OS
 *     file drag over a tab falls through to the normal upload flow.
 */
export const DOC_CARD_DRAG_TYPE = 'application/x-da-doc-id';

interface Props {
  /**
   * Either provide `reportId` (legacy: writes flow through `updateRagReport`)
   * OR provide `onUpdateDocuments` (host-driven, used by site-mode where
   * documents live on the site rather than on a report). If both are present
   * `onUpdateDocuments` wins.
   */
  reportId?: string;
  /**
   * Host-driven persistence. Receives the full new `documents` array after any
   * mutation (add, remove, rename, folder move, priority toggle). The host
   * decides where the data lives — site, report, etc. The "From Library"
   * picker is hidden when this mode is active.
   */
  onUpdateDocuments?: (updated: SiteDocument[]) => Promise<void>;
  /**
   * Site id — required when `onUpdateDocuments` writes to a site so the
   * library-index can be refreshed after edits (Documents_On_Site Prompt 3).
   * Optional in report-mode.
   */
  siteId?: string;
  /**
   * Report types the user has already produced (or planned) for this site.
   * Documents_On_Site Prompt 8-C — the embedded empty state renders a
   * "Recommended for {…} sites" checklist scoped to these types. Omit
   * for a generic empty state with no recommendation list.
   */
  recommendedReportTypes?: ReportTypeId[];
  /**
   * Optional skip handler for the rich empty state's "Skip — generate
   * from public data" CTA. When omitted the button is hidden so it
   * doesn't dead-end. Documents_On_Site Prompt 8-C.
   */
  onSkipUploads?: () => void;
  documents: SiteDocument[];
  open: boolean;
  onClose: () => void;
  onDocumentsChange?: (docs: SiteDocument[]) => void;
  embedded?: boolean; // Render inline instead of as modal
  /**
   * SET-B1 — Documents → Site Evidence bridge. When provided, each doc
   * surface (cards + list rows) renders a "View SED contributions →"
   * affordance that calls this handler with the doc's id. The parent
   * (SiteLandingPage) lifts the URL `?focus=doc:<id>` write + flips
   * `activeTab` to 'evidence'. Absence of this prop hides the affordance
   * — keeps DocumentLibrary callable in legacy / report-only mounts
   * that don't have a Site Evidence destination.
   */
  onJumpToEvidence?: (docId: string) => void;
  /**
   * DF-3 — DocLineageInspector → SED Variable Browser / UFC cross-link.
   * When the user clicks "Resolve in UFC →" on a cross-doc conflict
   * row inside the lineage inspector, the parent (SiteLandingPage)
   * sets `?focus=variable:<sectionId>.<fieldKey>` and flips the
   * active tab so the EvidenceConflictsPanel scrolls + ring-highlights
   * the matching group. Optional — when omitted the resolve button is
   * hidden in the inspector (legacy mounts without a parent router).
   */
  onJumpToVariable?: (sectionId: string, fieldKey: string) => void;
  /**
   * SET-B2 — Site Evidence → Documents bridge receiver. When supplied
   * AND matching a doc in the visible list, the library:
   *   1. scrolls the doc's row/card into view,
   *   2. opens the DetailDrawer for that doc,
   *   3. paints a ~2.8 s yellow ring on the row/card so the user's eye
   *      lands on the right one.
   * After the highlight fades, `onFocusHandled` is called so the
   * parent can clear the URL focus param (a refresh / back doesn't
   * re-trigger the highlight).
   */
  focusDocId?: string | null;
  /**
   * EDX-8 — `?focus=req:<id>` receiver. When non-null, pre-applies the
   * SED-contribution filter pill for the matching requirement id on
   * mount AND on subsequent value change. Calling `onFocusHandled`
   * after the receiver has done its work clears the URL param so a
   * subsequent toggle of the pill via UI doesn't fight a stale URL.
   */
  focusReqId?: string | null;
  /**
   * EDX-8 — `?focus=upload-for:<id>` receiver. When non-null:
   *   1. apply the SED-contribution filter pill for the requirement
   *      (so the user sees only matching docs once they finish
   *      uploading),
   *   2. scroll the dropzone into view + paint a 2.8s highlight ring
   *      to invite the upload action.
   * The requirement id is preserved in the filter pill so the new
   * upload's "Used in" / SED-contribution chip lights up immediately
   * once ingestion lands.
   */
  focusUploadForId?: string | null;
  /**
   * CMU-2 — `?focus=replace:<docId>::<requirementId>` receiver. When
   * non-null:
   *   1. Parse the composite id into `docId` + `requirementId`.
   *   2. Open the upload dropzone in "replace mode" — the next
   *      successful upload tags the new doc with
   *      `replacesDocId=<docId>` AND `intendedRequirementIds=[reqId]`,
   *      then soft-archives the old doc (sets `archived: true` +
   *      `archivedReason: 'replaced-by-<newDocId>'`).
   *   3. Scroll the dropzone into view + ring-highlight it (matches
   *      the `upload-for` receiver's affordance).
   *   4. Surface a small banner above the dropzone that names the
   *      doc being replaced + offers a "Cancel replace" escape.
   *
   * The banner-render path runs only while `focusReplaceDocId` is
   * non-null, so a `onFocusHandled` callback clears it after the
   * upload commits / the user cancels.
   *
   * Optional + back-compat — pre-CMU mounts that omit this prop see
   * no behavioural change (replace flow is opt-in by host).
   */
  focusReplaceDocId?: string | null;
  onFocusHandled?: () => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Per-extension colour class — paints the icon so the user can scan
 *  the library by file type at a glance. Aurora re-skin: tokenized onto
 *  semantic + teal-accent tokens (no raw Tailwind palette) so the icon
 *  ramp tracks the theme in both dark and light. The buckets still read
 *  as distinct families: doc=info(blue), sheet=success(green),
 *  markdown=primary(teal), image=conditional-high(warm), html=warning. */
const FILE_ICON_COLOR: Record<string, string> = {
  pdf: 'text-danger',
  docx: 'text-info',
  doc: 'text-info',
  xlsx: 'text-success',
  xls: 'text-success',
  csv: 'text-success',
  txt: 'text-text-muted',
  json: 'text-warning',
  md: 'text-primary',
  png: 'text-conditional-high',
  jpg: 'text-conditional-high',
  jpeg: 'text-conditional-high',
  gif: 'text-conditional-high',
  svg: 'text-conditional-high',
  mp4: 'text-info',
  mov: 'text-info',
  html: 'text-warning',
};

/** Per-extension icon component — Lucide ships shape-specific glyphs
 *  for the common families. Falls through to a generic File when an
 *  extension isn't mapped (we'd rather show *something* than render
 *  nothing — most uploads will land on FileGeneric in practice). */
const ICON_BY_EXT: Record<string, typeof FileText> = {
  pdf: FileType,
  doc: FileText,
  docx: FileText,
  xlsx: FileSpreadsheet,
  xls: FileSpreadsheet,
  csv: FileSpreadsheet,
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  svg: FileImage,
  bmp: FileImage,
  mp4: FileVideo,
  mov: FileVideo,
  webm: FileVideo,
  json: FileCode,
  md: FileCode,
  html: FileCode,
  htm: FileCode,
  xml: FileCode,
};

function getDocIcon(ext: string): typeof FileText {
  return ICON_BY_EXT[ext] ?? FileGeneric;
}

/** SET-D2 — collapse the per-extension surface into broad type buckets
 *  the user reasons about (pdf, docx, xlsx, pptx, image, kmz, other).
 *  Extension list is intentionally narrow so the filter dropdown stays
 *  short; anything not mapped falls into "other". */
function classifyDocType(fileName: string): string {
  const ext = (fileName.split('.').pop() ?? '').toLowerCase();
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx' || ext === 'doc') return 'docx';
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') return 'xlsx';
  if (ext === 'pptx' || ext === 'ppt') return 'pptx';
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'bmp', 'tiff'].includes(ext)) return 'image';
  if (ext === 'kmz' || ext === 'kml') return 'kmz';
  return 'other';
}

/**
 * DTC-1 — inline removable chip used in the active-filter row.
 * Each chip shows a label + an inline `×` button that removes
 * exactly one filter. Keyboard-accessible: the chip itself is a
 * button, so Enter / Space activate it. Tooltip on hover via
 * native `title`.
 */
function ChipRemovable({
  label,
  onRemove,
  testId,
}: {
  label: string;
  onRemove: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      title={`Remove filter: ${label}`}
      data-testid={testId}
      className="type-meta px-1.5 py-0.5 rounded-md border border-primary text-primary bg-primary/10 flex items-center gap-1 hover:bg-primary/20 transition-colors"
    >
      <span>{label}</span>
      <span aria-hidden="true">×</span>
    </button>
  );
}

const SET_D2_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pdf',   label: 'PDF' },
  { value: 'docx',  label: 'Word' },
  { value: 'xlsx',  label: 'Sheet' },
  { value: 'pptx',  label: 'Slides' },
  { value: 'image', label: 'Image' },
  { value: 'kmz',   label: 'KMZ' },
  { value: 'other', label: 'Other' },
];

/**
 * Outer shell for the document library. Hoisted to module scope so the
 * element TYPE is referentially stable across `DocumentLibrary` renders.
 *
 * Previously these two variants were arrow functions DEFINED INSIDE the
 * render body (`const Wrapper = embedded ? (...) => ... : (...) => ...`).
 * A fresh function identity each render makes React treat `<Wrapper>` as
 * a different component type every render, so it unmounts and remounts
 * the ENTIRE subtree on each keystroke — which blurred the search input
 * after every character (it can only keep focus if the DOM node
 * persists). Hoisting fixes the remount without changing any markup.
 */
function DocLibraryEmbeddedShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col h-full min-w-0 w-full overflow-hidden">{children}</div>
  );
}

function DocLibraryModalShell({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background w-[600px] max-h-[80vh] flex flex-col shadow-2xl rounded-lg border border-border" onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function DocumentLibrary({ reportId, onUpdateDocuments, siteId, recommendedReportTypes, onSkipUploads, documents, open, onClose, onDocumentsChange, embedded, onJumpToEvidence, onJumpToVariable, focusDocId, focusReqId, focusUploadForId, focusReplaceDocId, onFocusHandled }: Props) {
  const { updateRagReport, removeSiteDocument, bulkRemoveSiteDocuments, patchSiteDocument, appendSiteDocuments, renameFolder, moveSiteDocument } = useStore();
  // Site-mode = host-driven persistence. The "From Library" picker only makes
  // sense in report-mode (it scans other reports of the same site).
  const isSiteMode = !!onUpdateDocuments;

  // Documents_On_Site Prompt 6 — "Used in" badges per row. We pull the
  // site's reports out of the store once so each row's badge list is a
  // pure projection rather than a per-row store read.
  //
  // CRITICAL: the filter MUST live in useMemo, NOT inside the Zustand
  // selector. A selector that returns `{ reports: arr.filter(...), ... }`
  // hands React's useSyncExternalStore a fresh array reference on every
  // call; even useShallow can't help (it does Object.is per key, and
  // Object.is(filtered_a, filtered_b) is always false). React then sees
  // "snapshot changed" each render and loops → React #185. We instead
  // subscribe to the underlying stable arrays and project in useMemo.
  const allSites = useStore(s => s.siteRag.sites);
  const allReports = useStore(s => s.siteRag.reports);
  const usageContext = useMemo(() => {
    // Resolve the site we're operating on:
    //   - site-mode: explicit siteId prop, plus its document array
    //   - report-mode: walk back from the report to its owning site
    let site: import('../../types/site').SiteEntity | undefined;
    if (siteId) {
      site = allSites.find(x => x.id === siteId);
    } else if (reportId) {
      const r = allReports.find(rr => rr.id === reportId);
      site = siteForReport(allSites, r);
    }
    if (!site) return { reports: [] as typeof allReports, site: undefined };
    const reports = allReports.filter(r =>
      reportBelongsToSite(site!, r),
    );
    return { reports, site };
  }, [allSites, allReports, siteId, reportId]);
  const bodyOwnerUid = usageContext.site ? siteOwnerUid(usageContext.site, auth.currentUser?.uid ?? '') : '';
  const bodyLoader = useMemo(() => usageContext.site && bodyOwnerUid
    ? createSiteDocumentBodyLoader({ siteId: usageContext.site.id, ownerUid: bodyOwnerUid })
    : undefined, [usageContext.site?.id, bodyOwnerUid]);
  const binaryContext = useMemo(() => usageContext.site && bodyOwnerUid
    ? { siteId: usageContext.site.id, ownerUid: bodyOwnerUid } : undefined,
    [usageContext.site?.id, bodyOwnerUid]);
  const binaryLoader = useMemo(() => binaryContext ? createSiteDocumentOriginalLoader(binaryContext) : undefined, [binaryContext]);
  const aiOnlyLibrary = isAiOnlyEvidenceMode(usageContext.site?.evidenceMode);

  /**
   * Single persistence funnel — every mutation in this component calls this
   * with the new full array. Routes to the host callback when provided,
   * otherwise falls back to the legacy report-keyed write.
   *
   * In site-mode, we also fire a library-index refresh after persistence
   * so the auto-generated map reflects the edit (Documents_On_Site Prompt 3).
   * `mode` controls the cadence: 'upload' debounces (5s) so a 50-file batch
   * coalesces; 'edit'/'delete' refresh immediately.
   */
  const commit = async (updated: SiteDocument[], mode: 'edit' | 'upload' | 'delete' = 'edit') => {
    if (onUpdateDocuments) {
      await onUpdateDocuments(updated);
    } else if (reportId) {
      await updateRagReport(reportId, { documents: updated });
    }
    onDocumentsChange?.(updated);
    // Resolve the site id from the prop or, as a fallback, from any
    // existing library-index doc id encoded as `library-index-<siteId>`.
    if (isSiteMode) {
      const resolvedSiteId = siteId
        ?? updated.find(isLibraryIndexDoc)?.id.replace(/^library-index-/, '')
        ?? null;
      if (resolvedSiteId) {
        try {
          if (mode === 'upload') {
            const { refreshLibraryIndexDebounced } = await import('../../services/knowledge/libraryIndex');
            // 30s coalesces a fast-clicking session into one rebuild
            // (Documents_On_Site Prompt 7-B); the 📋 row's Refresh button
            // is the manual escape hatch.
            refreshLibraryIndexDebounced(resolvedSiteId);
          } else {
            // Fire-and-forget — UI shouldn't block on the rebuild.
            void refreshLibraryIndex(resolvedSiteId).catch(e =>
              console.warn('[libraryIndex] commit-side refresh failed:', (e as Error)?.message));
          }
        } catch (e) {
          console.warn('[libraryIndex] commit-side refresh skipped:', (e as Error)?.message);
        }
      }
    }
  };

  /**
   * DDL-3 — per-doc cross-array field update. Renames, folder moves and
   * the priority / includeInSed / includeInReport toggles all flow through
   * here in site-mode so the field write lands on canonical `site.documents[i]`
   * and mutable legacy report copies. The site-first, first-ID-wins document
   * union preserves the current site edit; immutable report snapshots retain
   * their original evidence and are never rewritten for a library edit.
   *
   * In report-mode or when no `siteId` is wired, we fall through to the
   * original `commit(updated)` path so legacy callers stay unaffected.
   */
  const commitFieldUpdate = async (
    docId: string,
    patch: Partial<SiteDocument>,
    mode: 'edit' | 'upload' | 'delete' = 'edit',
  ) => {
    if (siteId && onUpdateDocuments) {
      await patchSiteDocument(siteId, docId, patch);
      // The store now holds the server-confirmed canonical row. Never publish
      // an assumed local patch as a saved document to the host.
      onDocumentsChange?.(freshDocs());
      // A confirmed metadata edit must not launch a second, unchecked full-array
      // write for the derived library map. Upload/explicit rebuild paths own it.
      return;
    }
    // Legacy fallback — push the full computed array through commit().
    const updated = freshDocs().map(d => d.id === docId ? { ...d, ...patch } : d);
    await commit(updated, mode);
  };

  /**
   * Read fresh docs to avoid stale closures in long sequences of edits. In
   * site-mode and report-mode prefer their canonical store arrays; props
   * remain the fallback for legacy hosts without a store-backed entity.
   */
  const freshDocs = (): SiteDocument[] => {
    if (siteId && onUpdateDocuments) {
      return useStore.getState().siteRag.sites.find(site => site.id === siteId)?.documents ?? documents;
    }
    if (reportId) {
      return useStore.getState().siteRag.reports.find(r => r.id === reportId)?.documents || documents;
    }
    return documents;
  };

  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [customFolderName, setCustomFolderName] = useState('');
  const [customFolders, setCustomFolders] = useState<string[]>([]);
  // DMF-1 — id of the doc whose Move-to-folder dialog is open. Pre-DMF
  // this drove an inline popover which silently fell through to the row
  // click handler under some mount paths; now it gates a real modal
  // (MoveFolderDialog) so the move flow can't get clobbered by row
  // navigation.
  const [moveDialogDocId, setMoveDialogDocId] = useState<string | null>(null);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  // Inline doc viewer (PDF, DOCX, XLSX, image, video, text). One
  // dialog at the bottom of this component; clicking a row sets the
  // doc here. Action buttons inside the row stop propagation so
  // they don't open the viewer accidentally.
  const [viewerDoc, setViewerDoc] = useState<SiteDocument | null>(null);
  // DAT-2/3 — when the user clicks "Open at page" / "Verify" in the
  // DocLineageInspector, we forward the page number into DocViewerDialog
  // via its `initialPage` prop. Reset to `undefined` on every
  // top-of-row click so a normal preview still opens at page 1.
  const [viewerInitialPage, setViewerInitialPage] = useState<number | undefined>(undefined);
  // DAT-1/2 — id of the doc whose `DocLineageInspector` is currently
  // open. Distinct from the existing `detailDocId` (DocDetailDrawer):
  // the inspector is the doc-first lineage surface (Solves /
  // Populates / Importance), the drawer remains the summary/tag
  // editor. Both can be opened independently.
  const [lineageDocId, setLineageDocId] = useState<string | null>(null);
  // Detail drawer — surfaces summary, tags, relevance heatmap, and
  // "used in" for the selected doc. Opened via the Info icon on the
  // row's action cluster (single-click on the row body still opens
  // the viewer — preview is the hotter action).
  const [detailDocId, setDetailDocId] = useState<string | null>(null);
  // Resolve the live doc from `documents` so the drawer reflects edits
  // made through it (tag add, re-summarize) without stale-prop drift.
  const detailDoc = useMemo(
    () => (detailDocId ? documents.find(d => d.id === detailDocId) ?? null : null),
    [documents, detailDocId],
  );
  // DAT-1/2 — resolve the doc the inspector is open on. Lives in the
  // same single-source-of-truth pattern as `detailDoc` so edits to
  // the doc array don't drop the inspector context.
  const lineageDoc = useMemo(
    () => (lineageDocId ? documents.find(d => d.id === lineageDocId) ?? null : null),
    [documents, lineageDocId],
  );

  /** Drawer edits send only fields changed from the displayed row. A stale
   *  full document must not restore untouched flags or overwrite source data. */
  const handleDetailDocChange = async (updated: SiteDocument) => {
    if (siteId && onUpdateDocuments) {
      const displayed = documents.find(document => document.id === updated.id);
      const current = freshDocs().find(document => document.id === updated.id);
      if (!displayed || !current) throw new Error('This document is no longer available. Reload before editing.');
      const editableKeys = ['fileName', 'folder', 'priority', 'includeInSed', 'includeInReport', 'summary', 'tags'] as const;
      const changed = editableKeys.filter(key => JSON.stringify(displayed[key]) !== JSON.stringify(updated[key]));
      if (changed.some(key => JSON.stringify(displayed[key]) !== JSON.stringify(current[key]))) {
        throw new Error('These document fields changed while the editor was open. Reload before saving.');
      }
      if (changed.length) await commitFieldUpdate(updated.id, Object.fromEntries(changed.map(key => [key, updated[key]])));
      return;
    }
    await commit(freshDocs().map(document => document.id === updated.id ? updated : document));
  };

  /**
   * SET-B2 — Site Evidence → Documents bridge receiver. When the
   * parent passes `focusDocId` (driven by `?focus=doc:<id>` URL
   * param) AND the doc exists in the visible list, we:
   *   1. open the DetailDrawer for that doc,
   *   2. scroll the matching card/row into view,
   *   3. paint a yellow ring for ~2.8 s,
   *   4. call onFocusHandled so the URL param is cleared once the
   *      highlight has done its job.
   *
   * The 250 ms tick lets the folder containing the doc auto-expand
   * (we eagerly expand its folder below) and the DOM repaint before
   * we read the element.
   */
  React.useEffect(() => {
    if (!focusDocId) return;
    const targetDoc = documents.find(d => d.id === focusDocId);
    if (!targetDoc) return;
    // Open the detail drawer so the user lands on the doc's summary
    // + tags + relevance + "used in" panel — the same surface SET-D4
    // mounts the "How SED ingests this document" popover on.
    setDetailDocId(focusDocId);
    // Eagerly expand the folder this doc lives in so the row is
    // actually scrollable into view in list mode (cards mode is
    // always flat so this is a no-op there).
    const folderName = categorize(targetDoc);
    setExpandedFolders(prev => {
      if (prev.has(folderName)) return prev;
      const next = new Set(prev);
      next.add(folderName);
      return next;
    });
    let cleared = false;
    const handle = window.setTimeout(() => {
      try {
        const safe = CSS.escape(focusDocId);
        const root =
          document.querySelector(`[data-testid="doc-row-${safe}"]`) ??
          document.querySelector(`[data-testid="doc-card-${safe}"]`);
        if (root) {
          root.scrollIntoView({ behavior: 'smooth', block: 'center' });
          root.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
          window.setTimeout(() => {
            root.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
            if (!cleared) {
              cleared = true;
              onFocusHandled?.();
            }
          }, 2800);
        } else if (!cleared) {
          // No row to highlight (e.g. doc filtered out) — still clear
          // so the URL doesn't get stuck.
          cleared = true;
          onFocusHandled?.();
        }
      } catch {
        if (!cleared) {
          cleared = true;
          onFocusHandled?.();
        }
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [focusDocId, documents, onFocusHandled]);

  // List vs cards layout. Default = CARDS (founder QA r3 item 5c) — the
  // visual grid is the primary browse surface; the dense folder list
  // stays one click away on the view toggle in the filter row.
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('cards');

  // Founder QA r3 item 5d — folder tabs. `null` = the "All" tab.
  // Selecting a folder tab narrows BOTH layouts (cards grid + folder
  // list) to that folder. Tabs derive from the post-filter `folders`
  // grouping below, so search/filter chips and tabs compose.
  const [activeFolderTab, setActiveFolderTab] = useState<string | null>(null);
  // Item 5e — name of the folder tab a dragged document card is
  // currently hovering, for the drop affordance ring.
  const [dragOverFolderTab, setDragOverFolderTab] = useState<string | null>(null);

  // DAT-5 — sort affordance. 'recent' = legacy default (folders, by
  // uploadedAt within each folder); 'name' = within-folder by
  // filename asc; 'importance' = FLAT list ordered by the DAT-4
  // computeDocImportance index, with a `#N` rank badge on every row.
  // Opt-in. Switching to 'importance' bypasses the folder grouping
  // because the user explicitly asked to see "heaviest-lifting docs
  // first" — folder boundaries would partition the ranking.
  type SortMode = 'recent' | 'name' | 'importance';
  const [sortMode, setSortMode] = useState<SortMode>('recent');

  // DAT-FOLLOWUP-EXPORT-DOC-AUDIT — admin-only "Export doc audit (CSV)".
  // The chip lives next to the Sort selector but is hidden unless the
  // current user holds the admin claim. We check the claim ONCE on
  // mount (the token is force-refreshed inside `requireAdminClaim`) so
  // the chip flicker is minimal. Demoted admins re-mount → re-check
  // → chip hidden.
  const [isAdmin, setIsAdmin] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { requireAdminClaim } = await import('../../lib/requireAdminClaim');
        const ok = await requireAdminClaim();
        if (!cancelled) setIsAdmin(ok);
      } catch (e) {
        if (!cancelled) {
          console.debug('[DocumentLibrary] admin-claim probe failed:', (e as Error)?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // SET-D2 (2026-05-17) — user-facing search + filter controls.
  // Before this prompt the only useMemo filter (line ~154 above) was
  // over report-references for the per-row "Used in:" badges; no
  // doc-list filter existed. State is local (not persisted) so the
  // library re-opens in a known clean state each session.
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
  const [inSedOnly, setInSedOnly] = useState(false);

  // DTC-1 (2026-05-18) — controlled-open state for the "Filters ▾"
  // popover. Session-only — the popover re-opens closed on each
  // mount so the primary surface stays the ~80px bar.
  const [filtersPopoverOpen, setFiltersPopoverOpen] = useState(false);

  // EDX-4 — hide-noise default + revision dedup, persisted per-site.
  // Both toggles default ON when no per-site preference is found, so
  // a fresh user sees a clean list immediately. Key convention matches
  // `lib/lastSubTabMemory.ts:29` (`siterag:<feature>:<siteId>`).
  const noiseKey = siteId ? `siterag:hideNoise:${siteId}` : null;
  const revisionKey = siteId ? `siterag:groupRevisions:${siteId}` : null;
  const [hideNoise, setHideNoise] = useState<boolean>(() => {
    if (!noiseKey) return true;
    try {
      const raw = localStorage.getItem(noiseKey);
      if (raw == null) return true;
      return raw === '1' || raw === 'true';
    } catch { return true; }
  });
  const [groupRevisions, setGroupRevisions] = useState<boolean>(() => {
    if (!revisionKey) return true;
    try {
      const raw = localStorage.getItem(revisionKey);
      if (raw == null) return true;
      return raw === '1' || raw === 'true';
    } catch { return true; }
  });
  React.useEffect(() => {
    if (!noiseKey) return;
    try { localStorage.setItem(noiseKey, hideNoise ? '1' : '0'); } catch { /* quota */ }
  }, [hideNoise, noiseKey]);
  React.useEffect(() => {
    if (!revisionKey) return;
    try { localStorage.setItem(revisionKey, groupRevisions ? '1' : '0'); } catch { /* quota */ }
  }, [groupRevisions, revisionKey]);

  // EDX-4 — pre-compute the noise-hidden + revision-grouped views so
  // the chip-row counters can read the deltas without re-running the
  // predicates on every render.
  const noiseHiddenCount = useMemo(
    () => documents.filter(d => !isLibraryIndexDoc(d) && isNoisyDoc(d)).length,
    [documents],
  );
  // Revision groups: docs sharing `contentHash` collapse to one
  // canonical row (latest `uploadedAt` wins). Docs without a hash
  // (pre-Documents_On_Site Prompt 7-C uploads) stay ungrouped.
  type RevisionGroup = { canonical: SiteDocument; earlier: SiteDocument[] };
  const revisionGroupsByCanonical = useMemo(() => {
    const groups = new Map<string, RevisionGroup>();
    if (!groupRevisions) return groups;
    const byHash = new Map<string, SiteDocument[]>();
    for (const d of documents) {
      if (isLibraryIndexDoc(d)) continue;
      const h = d.contentHash;
      if (!h) continue;
      const list = byHash.get(h) ?? [];
      list.push(d);
      byHash.set(h, list);
    }
    for (const [, list] of byHash) {
      if (list.length < 2) continue;
      const sorted = [...list].sort((a, b) => {
        const ta = a.uploadedAt ? Date.parse(a.uploadedAt) : 0;
        const tb = b.uploadedAt ? Date.parse(b.uploadedAt) : 0;
        return tb - ta;
      });
      const canonical = sorted[0]!;
      const earlier = sorted.slice(1);
      groups.set(canonical.id, { canonical, earlier });
    }
    return groups;
  }, [documents, groupRevisions]);
  // Doc ids that are "swallowed" by a canonical row — these get hidden
  // in default group-revisions mode and surfaced behind the "N earlier
  // revisions" expander on the canonical row.
  const swallowedRevisionIds = useMemo(() => {
    const set = new Set<string>();
    for (const grp of revisionGroupsByCanonical.values()) {
      for (const d of grp.earlier) set.add(d.id);
    }
    return set;
  }, [revisionGroupsByCanonical]);
  const revisionGroupedCount = swallowedRevisionIds.size;

  // Per-canonical expander state (which revision groups the user has
  // expanded inline). Session-only — collapsing again on tab re-open
  // mirrors the "noise+grouped is the clean default" framing.
  const [expandedRevisionGroups, setExpandedRevisionGroups] = useState<Set<string>>(new Set());
  const toggleRevisionGroup = (canonicalId: string) => {
    setExpandedRevisionGroups(prev => {
      const next = new Set(prev);
      if (next.has(canonicalId)) next.delete(canonicalId);
      else next.add(canonicalId);
      return next;
    });
  };

  // EDX-5 — SED-contribution + USED-IN filter pills. URL-persisted via
  // `?filter[contrib]=…&filter[usedin]=…` (comma-separated) so deep
  // links round-trip and the EDX-8 bridges can pre-apply the pill on
  // tab arrival. Initial state reads the URL once; subsequent updates
  // re-write through `useUrlSearchParamSync`.
  const [contribFilter, setContribFilter] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = new URLSearchParams(window.location.search).get('filter[contrib]');
      if (!raw) return new Set();
      return new Set(raw.split(',').map(s => s.trim()).filter(Boolean));
    } catch { return new Set(); }
  });
  const [usedInFilter, setUsedInFilter] = useState<Set<ReportTypeId>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = new URLSearchParams(window.location.search).get('filter[usedin]');
      if (!raw) return new Set();
      return new Set(raw.split(',').map(s => s.trim()).filter(Boolean) as ReportTypeId[]);
    } catch { return new Set(); }
  });
  useUrlSearchParamSync(
    'filter[contrib]',
    contribFilter.size > 0 ? [...contribFilter].sort().join(',') : null,
  );
  useUrlSearchParamSync(
    'filter[usedin]',
    usedInFilter.size > 0 ? [...usedInFilter].sort().join(',') : null,
  );

  // EDX-8 — `?focus=req:<id>` receiver. Pre-applies the SED-contribution
  // filter pill on arrival so a click on a Coverage Matrix cell lands
  // the user on a doc list narrowed to the requirement they came from.
  // We REPLACE the filter (not union) so the bridge action behaves
  // predictably even when the user already had unrelated pills active.
  React.useEffect(() => {
    if (!focusReqId) return;
    setContribFilter(new Set([focusReqId]));
    // `onFocusHandled` is intentionally NOT called here — the URL
    // param is the source of truth for "the filter is active because
    // I just arrived via the bridge". The Cross-Tab breadcrumb above
    // surfaces a Clear button that clears both the filter pill AND
    // the URL focus param via `clearFocus` upstream. Firing
    // `onFocusHandled` here would strip the URL param immediately,
    // which would make the breadcrumb vanish and confuse the user.
  }, [focusReqId]);
  // EDX-8 — `?focus=upload-for:<id>` receiver. Same SED-contribution
  // filter pre-apply as `req:<id>`, plus scroll the dropzone into
  // view and paint a 2.8s ring so the eye lands on the upload
  // affordance.
  React.useEffect(() => {
    if (!focusUploadForId) return;
    setContribFilter(new Set([focusUploadForId]));
    let cleared = false;
    const handle = window.setTimeout(() => {
      try {
        const zone = document.querySelector('[data-testid="doc-library-dropzone"]');
        if (zone) {
          zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
          zone.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
          window.setTimeout(() => {
            zone.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
            if (!cleared) {
              cleared = true;
              // Clear ONLY the URL focus param — the filter pill stays
              // applied so the user sees their upload land on the right
              // pre-filtered slice after ingestion.
              onFocusHandled?.();
            }
          }, 2800);
        } else if (!cleared) {
          cleared = true;
          onFocusHandled?.();
        }
      } catch {
        if (!cleared) {
          cleared = true;
          onFocusHandled?.();
        }
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [focusUploadForId, onFocusHandled]);

  // CMU-2 — `?focus=replace:<docId>::<requirementId>` receiver. Parsed
  // memoised so the `handleUpload` closure + the inline banner can
  // both consult it without re-parsing on every keystroke. Returns
  // `null` when the prop is null OR malformed (defensive — guards
  // against a hand-crafted deep link with a missing requirementId).
  const replaceTarget = React.useMemo<{ docId: string; requirementId: string; doc: SiteDocument | undefined } | null>(() => {
    if (!focusReplaceDocId) return null;
    const sep = focusReplaceDocId.indexOf('::');
    if (sep <= 0) return null;
    const docId = focusReplaceDocId.slice(0, sep);
    const requirementId = focusReplaceDocId.slice(sep + 2);
    if (!docId || !requirementId) return null;
    const doc = documents.find(d => d.id === docId);
    return { docId, requirementId, doc };
  }, [focusReplaceDocId, documents]);

  // CMU-2 — replace-mode side effects. Pre-applies the SED-contribution
  // pill so the user sees only matching docs after the upload, and
  // ring-highlights the dropzone the same way `upload-for` does.
  React.useEffect(() => {
    if (!replaceTarget) return;
    setContribFilter(new Set([replaceTarget.requirementId]));
    const handle = window.setTimeout(() => {
      try {
        const zone = document.querySelector('[data-testid="doc-library-dropzone"]');
        if (zone) {
          zone.scrollIntoView({ behavior: 'smooth', block: 'center' });
          zone.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
          window.setTimeout(() => {
            zone.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
          }, 2800);
        }
      } catch { /* receiver is best-effort */ }
    }, 250);
    return () => window.clearTimeout(handle);
    // We intentionally DO NOT call `onFocusHandled` here — the URL
    // param is the source-of-truth for "replace mode is active"; it
    // gets cleared on successful upload commit OR via the banner's
    // Cancel button.
  }, [replaceTarget]);

  // EDX-5 — checklist requirements available as SED-contribution pill
  // options. Walks the union across every active/beta report type so
  // the pill row is comprehensive even on sites with no reports yet.
  const contribOptions = useMemo(() => {
    const allTypes = (Object.values(REPORT_TYPE_REGISTRY)
      .filter(t => t.status === 'active' || t.status === 'beta')
      .map(t => t.id)) as ReportTypeId[];
    return getDocumentChecklistAcross(allTypes);
  }, []);
  // EDX-5 — USED-IN pill options: the same active/beta type list,
  // collapsed via the REPORT_TYPE_REGISTRY (one chip per type).
  const usedInOptions = useMemo(
    () => Object.values(REPORT_TYPE_REGISTRY).filter(t => t.status === 'active' || t.status === 'beta'),
    [],
  );

  // EDX-5 — toggle helpers for the two pill sets.
  const toggleContrib = (itemId: string) => {
    setContribFilter(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };
  const toggleUsedIn = (typeId: ReportTypeId) => {
    setUsedInFilter(prev => {
      const next = new Set(prev);
      if (next.has(typeId)) next.delete(typeId);
      else next.add(typeId);
      return next;
    });
  };

  // DTC-3 — shared clear-all helper. Resets every filter (search,
  // tag, type, in-SED, SED-contribution, applicable-to) AND strips
  // the URL filter params so a reload doesn't restore them. Wired
  // to BOTH the top "Clear all" affordance in the active-chip row
  // AND the buried `doc-library-filter-empty-clear` button. Before
  // this helper the empty-state button only cleared four of six
  // filter types — clicking GEOTECHNICAL → "0 of 85" → "Clear
  // filters" was a no-op for the active contribution filter.
  const clearAllFilters = useCallback(() => {
    setSearch('');
    setTagFilter(new Set());
    setTypeFilter(new Set());
    setInSedOnly(false);
    setContribFilter(new Set());
    setUsedInFilter(new Set());
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('filter[contrib]');
        url.searchParams.delete('filter[usedin]');
        url.searchParams.delete('focus');
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore — useUrlSearchParamSync will catch up */ }
    }
  }, []);

  // EDX-5 — pre-compute per-doc contribution membership so the row
  // filter is O(N) rather than O(N × M) on every render. The set
  // contains every (docId, requirementId) pair that matches the
  // legacy tag-pattern matcher; the filter checks "does this doc hit
  // any of the active contrib pills?". Active pills combine OR
  // within a category, AND across categories.
  const contribMembership = useMemo(() => {
    const set = new Set<string>();
    for (const doc of documents) {
      if (isLibraryIndexDoc(doc)) continue;
      for (const item of contribOptions) {
        if (docMatchesPatterns(doc, item.relevanceTagPattern)) {
          set.add(`${doc.id}::${item.id}`);
        }
      }
    }
    return set;
  }, [documents, contribOptions]);

  // DTC-4 — per-pill doc counts so the SED-contribution chips surface
  // how many docs each requirement hits BEFORE the user clicks. Sorted
  // by count desc so high-signal categories float to the top — zero-
  // count pills sink below (still visible so the user can plan an
  // upload, but dimmed in the popover render).
  const contribOptionsWithCount = useMemo(() => {
    const counts = new Map<string, number>();
    for (const doc of documents) {
      if (isLibraryIndexDoc(doc)) continue;
      for (const item of contribOptions) {
        if (contribMembership.has(`${doc.id}::${item.id}`)) {
          counts.set(item.id, (counts.get(item.id) ?? 0) + 1);
        }
      }
    }
    return contribOptions
      .map(item => ({ ...item, count: counts.get(item.id) ?? 0 }))
      .sort((a, b) => b.count - a.count);
  }, [documents, contribOptions, contribMembership]);

  // DTC-4 — same shape for the Applicable-to pills. We compute the
  // matcher result for EVERY doc here (independent of usedInFilter
  // size) so the count badge is correct even before the user opens
  // the popover. `usedInByDoc` below still short-circuits when the
  // filter is empty — that's the hot path for the row filter, not
  // count display.
  const usedInOptionsWithCount = useMemo(() => {
    const counts = new Map<string, number>();
    if (usageContext.site) {
      for (const d of documents) {
        if (isLibraryIndexDoc(d)) continue;
        const types = applicableReportTypes(d);
        for (const t of types) {
          counts.set(t, (counts.get(t) ?? 0) + 1);
        }
      }
    }
    return usedInOptions
      .map(t => ({ ...t, count: counts.get(t.id) ?? 0 }))
      .sort((a, b) => b.count - a.count);
  }, [documents, usedInOptions, usageContext]);

  // Drop-zone affordance. `isDraggingFiles` flips when the user starts
  // dragging files anywhere over the page so the zone visibly invites
  // a drop without forcing the user to aim precisely. Window-scoped
  // listener so it works whether the drag started inside or outside
  // our component.
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  React.useEffect(() => {
    let dragCounter = 0;
    const isFileDrag = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      dragCounter += 1;
      setIsDraggingFiles(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      dragCounter = Math.max(0, dragCounter - 1);
      if (dragCounter === 0) setIsDraggingFiles(false);
    };
    const onDrop = () => { dragCounter = 0; setIsDraggingFiles(false); };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, []);

  const totalBytes = useMemo(
    () => documents.reduce((s, d) => s + (d.sizeBytes ?? 0), 0),
    [documents],
  );

  // Library Map (auto-generated) — founder QA r3 item 4: no longer
  // surfaced as a user-facing pinned card. The library-index doc still
  // exists and is excluded from folder bucketing / filters below; its
  // refresh remains on upload, deletion and explicit rebuild workflows.
  // Checked per-document metadata edits do not issue derived full-array writes.

  // SET-D2 — compute the universe of tags from the full doc list (so
  // the filter dropdown stays stable as the user narrows the view).
  const availableTags = useMemo(() => {
    const set = new Set<string>();
    for (const d of documents) {
      for (const t of d.tags ?? []) set.add(t);
    }
    return [...set].sort();
  }, [documents]);

  // SET-D2 + EDX-4 + EDX-5 — apply search + type/tag/in-SED + hide-noise +
  // revision-dedup + SED-contribution + USED-IN filters before folder
  // grouping. The library-index doc is intentionally NOT filtered (it
  // never renders — the `isLibraryIndexDoc(doc)` guard inside the folder
  // loop keeps it out of every folder bucket). Pills combine OR within a
  // row, AND across rows.
  const filterActive = !!search.trim()
    || tagFilter.size > 0
    || typeFilter.size > 0
    || inSedOnly
    || contribFilter.size > 0
    || usedInFilter.size > 0;
  // DTC-1 — count of filters that live inside the "Filters ▾" popover
  // (i.e. excluding the always-visible search input). Drives the
  // badge on the popover trigger so the user sees at a glance how
  // many advanced filters are active without opening the popover.
  const advancedFilterCount =
    typeFilter.size +
    tagFilter.size +
    contribFilter.size +
    usedInFilter.size +
    (inSedOnly ? 1 : 0);
  // Pre-resolve "applicable-to" per doc so the filter is O(1) per row.
  // DRT-2 — uses `applicableReportTypes(doc)` to match the new per-row pill
  // semantics (`doc.relevance` classifier scores). Previously this read
  // `usedInReportTypes` (report-generation history) which made the
  // ENVIRONMENTAL/WATER/INTERCONNECTION filter chips no-op on sites that
  // only ever generated a Feasibility report — see the DRT pack
  // (master plan row 0.99199).
  const usedInByDoc = useMemo(() => {
    const map = new Map<string, Set<ReportTypeId>>();
    if (!usageContext.site || usedInFilter.size === 0) return map;
    for (const d of documents) {
      const types = applicableReportTypes(d);
      map.set(d.id, new Set(types as ReportTypeId[]));
    }
    return map;
  }, [documents, usageContext, usedInFilter.size]);
  const visibleDocuments = useMemo(() => {
    const q = search.toLowerCase().trim();
    return documents.filter(d => {
      // DDL-FOLLOWUP-RESTORE — soft-deleted docs are hidden from EVERY
      // default Documents-tab view (including the library-index pinned
      // row). The admin Ghost Docs panel surfaces them with Restore.
      // We check BEFORE the library-index escape hatch so a tombstoned
      // library-index doc doesn't sneak through.
      if (!isLiveDoc(d)) return false;
      // Library-index doc must always remain available to the pinned
      // row regardless of filter state. Just skip it from the filter
      // logic; the folder loop will skip it via isLibraryIndexDoc too.
      if (isLibraryIndexDoc(d)) return true;
      // EDX-4 — hide-noise default. `z. Archive/`, `.url`, `.eml`,
      // 0-byte, Office `~$` lock files. Always toggleable from the
      // chip row above.
      if (hideNoise && isNoisyDoc(d)) return false;
      // EDX-4 — swallow non-canonical revisions when group-revisions
      // is on. The canonical row still surfaces; clicking the
      // "N earlier revisions" expander reveals these inline.
      if (groupRevisions && swallowedRevisionIds.has(d.id)) return false;
      if (!filterActive) return true;
      if (q) {
        const haystack = [
          d.fileName ?? '',
          d.summary ?? '',
          ...(d.tags ?? []),
        ].join('\n').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (tagFilter.size > 0) {
        const docTags = new Set(d.tags ?? []);
        let hit = false;
        for (const t of tagFilter) { if (docTags.has(t)) { hit = true; break; } }
        if (!hit) return false;
      }
      if (typeFilter.size > 0 && !typeFilter.has(classifyDocType(d.fileName))) return false;
      if (inSedOnly && d.includeInSed === false) return false;
      // EDX-5 — SED-contribution pill. OR within the set.
      if (contribFilter.size > 0) {
        let hit = false;
        for (const itemId of contribFilter) {
          if (contribMembership.has(`${d.id}::${itemId}`)) { hit = true; break; }
        }
        if (!hit) return false;
      }
      // EDX-5 — USED-IN pill. OR within the set.
      if (usedInFilter.size > 0) {
        const types = usedInByDoc.get(d.id) ?? new Set<ReportTypeId>();
        let hit = false;
        for (const t of usedInFilter) {
          if (types.has(t)) { hit = true; break; }
        }
        if (!hit) return false;
      }
      return true;
    });
  }, [documents, filterActive, search, tagFilter, typeFilter, inSedOnly, hideNoise, groupRevisions, swallowedRevisionIds, contribFilter, usedInFilter, contribMembership, usedInByDoc]);

  // Count of visible docs (excluding library-index, since the library-
  // index doc is shown in its own pinned row and not in folder totals).
  const visibleNonIndexCount = useMemo(
    () => visibleDocuments.filter(d => !isLibraryIndexDoc(d)).length,
    [visibleDocuments],
  );
  const totalNonIndexCount = useMemo(
    () => documents.filter(d => !isLibraryIndexDoc(d)).length,
    [documents],
  );

  // DAT-5 — importance rank map keyed by doc id. Computed against
  // the full filtered set so a `#N of M` badge is meaningful even
  // when other filters are on. SED is intentionally undefined here —
  // the inspector loads SED lazily for the breakdown; the row-render
  // path relies on `doc.populatedSlotCount` (UPE-cached) which is
  // exactly what the slot term reads when SED is absent. Recomputed
  // when the visible set or sort mode changes, but only meaningful
  // when sortMode === 'importance' — kept memoised so flipping to
  // any other mode doesn't pay the cost.
  //
  // Persisted scores remain an O(1) cache when present. Legacy / unscored
  // documents use the same pure formula, without a mount-time save.
  const importanceRankByDocId = useMemo<Map<string, RankedDoc>>(() => {
    if (sortMode !== 'importance') return new Map();
    const docs = visibleDocuments.filter(d => !isLibraryIndexDoc(d));
    return rankDocsByImportanceMap(docs, undefined);
  }, [visibleDocuments, sortMode]);

  // Opening or re-rendering the library is read-only. Missing importance
  // scores use the formula in rankDocsByImportanceMap; persisting one derived
  // score through patchSiteDocument would also rewrite every full report body.
  // Explicit user edits still use the existing cross-array persistence funnel.

  // DAT-5 — flat ranked list rendered when sortMode === 'importance'.
  // Replaces the folder grouping so high-impact docs cluster at the
  // top regardless of category bucket. Folder grouping is restored
  // automatically when the user switches sort back to Recent / Name.
  const flatRankedDocs = useMemo<SiteDocument[]>(() => {
    if (sortMode !== 'importance') return [];
    const docs = visibleDocuments.filter(d => !isLibraryIndexDoc(d));
    const ranked = [...docs].sort((a, b) => {
      const ra = importanceRankByDocId.get(a.id)?.rank ?? Number.MAX_SAFE_INTEGER;
      const rb = importanceRankByDocId.get(b.id)?.rank ?? Number.MAX_SAFE_INTEGER;
      return ra - rb;
    });
    return orderEmailAttachments(ranked);
  }, [visibleDocuments, sortMode, importanceRankByDocId]);

  // Group documents by category folder (library-map doc excluded — it has
  // its own pinned row). EDX-4 — when group-revisions is on and the
  // user has expanded a canonical row's earlier-revisions, splice the
  // earlier docs in right after the canonical so the inline expansion
  // reveals them in the same folder bucket.
  const folders = useMemo(() => {
    // DAT-5 — when sortMode === 'importance', short-circuit the folder
    // grouping into a single synthetic bucket holding the flat ranked
    // list. Keeps every downstream renderer (list rows, cards, header
    // row, doc-row chips) intact — only the bucketisation changes.
    if (sortMode === 'importance') {
      return [['By importance', flatRankedDocs] as [string, SiteDocument[]]];
    }
    const map = new Map<string, SiteDocument[]>();
    const visibleById = new Map(visibleDocuments.map(d => [d.id, d]));
    for (const doc of visibleDocuments) {
      if (isLibraryIndexDoc(doc)) continue;
      const cat = categorize((doc.parentDocId && visibleById.get(doc.parentDocId)) || doc);
      if (!map.has(cat)) map.set(cat, []);
      const bucket = map.get(cat)!;
      bucket.push(doc);
      // EDX-4 inline expansion — only fires when group-revisions is
      // on AND this doc is a canonical row whose group the user
      // toggled open. Earlier revisions follow the canonical.
      if (groupRevisions && expandedRevisionGroups.has(doc.id)) {
        const grp = revisionGroupsByCanonical.get(doc.id);
        if (grp) bucket.push(...grp.earlier);
      }
    }
    // Include empty custom folders so they persist in the UI
    for (const f of customFolders) {
      if (!map.has(f)) map.set(f, []);
    }
    // DAT-5 — within-folder ordering driven by sortMode. 'recent'
    // (default) preserves the historical newest-first cadence;
    // 'name' switches to alphabetical. 'importance' bypasses folder
    // grouping entirely (rendered from `flatRankedDocs`), so this
    // branch is not reached in that mode.
    if (sortMode === 'name') {
      for (const bucket of map.values()) {
        bucket.sort((a, b) => a.fileName.localeCompare(b.fileName));
      }
    } else if (sortMode === 'recent') {
      for (const bucket of map.values()) {
        bucket.sort((a, b) => (Date.parse(b.uploadedAt) || 0) - (Date.parse(a.uploadedAt) || 0));
      }
    }
    for (const [name, bucket] of map) map.set(name, orderEmailAttachments(bucket));
    // Sort folders alphabetically, but "General" last
    return [...map.entries()].sort(([a], [b]) => {
      if (a === 'General') return 1;
      if (b === 'General') return -1;
      return a.localeCompare(b);
    });
    // SET-D2 — dep includes `visibleDocuments` so filter state churn
    // re-groups; `customFolders` keeps user-added empty folders sticky.
    // DAT-5 — `flatRankedDocs` + `sortMode` so the importance branch
    // recomputes when the rank map changes.
  }, [visibleDocuments, customFolders, groupRevisions, expandedRevisionGroups, revisionGroupsByCanonical, sortMode, flatRankedDocs]);

  const toggleFolder = (name: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Expand all folders by default
  React.useEffect(() => {
    setExpandedFolders(new Set(folders.map(([name]) => name)));
  }, [folders.length]);

  // Item 5d — folder-tab narrowing, applied to BOTH layouts. `null`
  // (the "All" tab) passes everything through. Composes with the
  // search/filter pipeline because it runs on the post-filter grouping.
  const visibleFolders = useMemo(
    () => (activeFolderTab == null
      ? folders
      : folders.filter(([name]) => name === activeFolderTab)),
    [folders, activeFolderTab],
  );

  // If the active folder tab disappears (filters narrowed it away, the
  // folder emptied, or sort switched to the flat importance ranking),
  // fall back to "All" rather than showing a blank library.
  React.useEffect(() => {
    if (activeFolderTab != null && !folders.some(([name]) => name === activeFolderTab)) {
      setActiveFolderTab(null);
    }
  }, [folders, activeFolderTab]);

  /**
   * Item 5e — drop a dragged document card onto a folder tab to MOVE it.
   * Site-mode routes through the store's `moveSiteDocument` (the same
   * owner-path-safe cross-array action MoveFolderDialog uses post-#17);
   * legacy report-mode falls back to the commitFieldUpdate funnel. Folder
   * metadata does not launch a derived full-array library-map write.
   */
  const handleDocDropOnFolder = async (folderName: string, docId: string) => {
    const doc = freshDocs().find(d => d.id === docId);
    if (!doc) return;
    if ((doc.folder ?? categorize(doc)) === folderName) return; // no-op move
    try {
      if (siteId) {
        await moveSiteDocument(siteId, docId, folderName);
        onDocumentsChange?.(freshDocs());
      } else {
        await commitFieldUpdate(docId, { folder: folderName });
      }
      toast.success(`Moved "${doc.fileName}" to ${folderName}`);
    } catch (e) {
      toast.error(`Move failed: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  };

  const handleDelete = async (docId: string) => {
    // DDL-FOLLOWUP-CONFIRM-DIALOG — guard the delete with a confirm modal
    // so a misclick is recoverable. The modal copy includes the file name,
    // the count of reports that still reference the doc (so the user
    // understands the blast radius), and the SOFT_DELETE_TTL_DAYS window
    // during which the doc remains restorable from the Ghost Docs admin
    // panel. The actual delete is now SOFT — `removeSiteDocument` stamps
    // `deletedAt` / `deletedBy` in place; restore is one click in the
    // admin panel for the next 7 days; the purge cron hard-deletes after.
    const target = freshDocs().find(d => d.id === docId);
    // displayFileName returns a { leaf, breadcrumb } OBJECT — interpolating
    // it rendered the dialog title as "Delete '[object Object]'?" (and the
    // `|| target.fileName` fallback never fired because an object is always
    // truthy). The leaf is the display name.
    const fileName = (target ? displayFileName(target).leaf || target.fileName : null) ?? 'this document';
    // Reference count = number of site-scoped reports that still hold the
    // doc id in `documents[]` OR `documentRefs[]`. Pure projection over
    // the store — agrees with what `removeSiteDocument`'s cross-array
    // sweep actually touches.
    const refCount = siteId
      ? countReferencingReports(allReports, siteId, docId)
      : 0;

    // Evidence impact — what this document put into the SED. Deleting used
    // to be library-only: extracted fields kept scoring against a document
    // that no longer existed, with no warning. The dialog now states the
    // real blast radius, and confirming removes the machine contributions
    // (human-reviewed values survive; see scrubDocFromSed).
    let sedImpact: import('../../lib/coverage/docSedContributions').DocSedContributions | null = null;
    if (siteId) {
      try {
        const st = useStore.getState();
        const site = st.siteRag.sites.find(s => s.id === siteId);
        if (site?.sedId) {
          const { loadSedById } = await import('../../services/sedLoader');
          const sed = await loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
          if (sed) {
            const { sedContributionsOfDoc } = await import('../../lib/coverage/docSedContributions');
            sedImpact = sedContributionsOfDoc(sed, docId);
          }
        }
      } catch (e) {
        console.warn('[handleDelete] SED impact preview failed:', (e as Error)?.message);
      }
    }
    const { describeDocSedImpact } = await import('../../lib/coverage/docSedContributions');

    // The user's choice, captured from the checkbox inside the dialog body.
    // openConfirmDialog resolves a boolean only, so the extra decision rides
    // a mutable ref the JSX writes into. Default ON: orphaned machine values
    // scoring against a deleted document is the surprising outcome.
    const scrubChoice = { remove: true };
    const removableToShow = sedImpact?.removableFields.slice(0, 8) ?? [];
    const removableOverflow = (sedImpact?.removableFields.length ?? 0) - removableToShow.length;

    const ok = await openConfirmDialog({
      title: `Delete '${fileName}'?`,
      body: (
        <>
          <p>
            This document is referenced by <b>{refCount}</b>{' '}
            report{refCount === 1 ? '' : 's'} and will be restorable
            for <b>{SOFT_DELETE_TTL_DAYS} days</b> from the Ghost Docs
            admin panel.
          </p>
          <div className="mt-2" data-testid="delete-sed-impact">
            {sedImpact == null ? (
              <p className="text-warning">
                Evidence impact could not be checked — if this document
                populated any fields, they will keep referencing it after
                deletion.
              </p>
            ) : !sedImpact.hasAny ? (
              <p>This document has not populated any evidence fields.</p>
            ) : (
              <>
                <p><b>Evidence impact:</b> {describeDocSedImpact(sedImpact)}</p>
                {removableToShow.length > 0 && (
                  <ul
                    className="mt-1 space-y-0.5 rounded-sm border border-border-subtle bg-surface-2/60 p-2 text-[11px]"
                    data-testid="delete-sed-impact-fields"
                  >
                    {removableToShow.map(f => (
                      <li key={`${f.sectionId}.${f.fieldKey}`} className="flex justify-between gap-3">
                        <span className="text-text-muted">{f.sectionId}.{f.fieldKey}</span>
                        <span className="font-mono truncate max-w-[14rem]">{String(f.value)}</span>
                      </li>
                    ))}
                    {removableOverflow > 0 && (
                      <li className="text-text-muted">…and {removableOverflow} more</li>
                    )}
                  </ul>
                )}
                {sedImpact.keptHumanFields.length > 0 && (
                  <p className="mt-1 text-text-muted">
                    {sedImpact.keptHumanFields.length} human-reviewed value{sedImpact.keptHumanFields.length === 1 ? '' : 's'}{' '}
                    (accepted or corrected) will be kept either way.
                  </p>
                )}
                <label className="mt-2 flex items-start gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    defaultChecked
                    data-testid="delete-sed-scrub-choice"
                    onChange={(e) => { scrubChoice.remove = e.target.checked; }}
                    className="mt-0.5"
                  />
                  <span>
                    Also remove these extracted field inputs (recommended).
                    Untick to keep the data — it will remain in evidence with
                    no source document behind it. Restoring the document later
                    re-extracts on the next scan.
                  </span>
                </label>
              </>
            )}
          </div>
        </>
      ),
      confirmLabel: `Delete (restorable ${SOFT_DELETE_TTL_DAYS}d)`,
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;

    // DDL-1 + DDL-FOLLOWUP-RESTORE — in site-mode (Documents tab on
    // SiteLandingPage), route through the store's `removeSiteDocument`
    // action, which now performs a SOFT cross-array delete (stamps
    // deletedAt/deletedBy on site.documents + every report.documents on
    // the site). The commit()/onUpdateDocuments path stays untouched
    // for rename / folder move / priority toggle / bulk edit.
    if (siteId && onUpdateDocuments) {
      await removeSiteDocument(siteId, docId);
      // Evidence scrub — remove the doc's machine contributions so nothing
      // keeps scoring against a document that no longer exists. Best-effort:
      // a scrub failure must not block the delete (the doc is already soft
      // deleted); the failure is surfaced so the user knows evidence still
      // references it.
      if (sedImpact?.hasAny && scrubChoice.remove) {
        const scrub = await useStore.getState().scrubDocumentFromSed(siteId, docId);
        if (!scrub.ok) {
          toast.error(`Document deleted. Evidence cleanup could not be confirmed. ${scrub.reason ?? 'Reload evidence before retrying.'}`);
        } else if ((scrub.removedFieldCount ?? 0) > 0 || (scrub.downgradedSections?.length ?? 0) > 0) {
          toast.info(
            `Evidence updated: removed ${scrub.removedFieldCount ?? 0} extracted field${(scrub.removedFieldCount ?? 0) === 1 ? '' : 's'}`
            + ((scrub.downgradedSections?.length ?? 0) > 0
              ? `; ${scrub.downgradedSections!.length} section${scrub.downgradedSections!.length === 1 ? '' : 's'} reverted to "not collected".`
              : '.'),
          );
        }
      }
      // Library-index refresh — the store action is intentionally a pure
      // persistence funnel (per the comment on `removeSiteDocument`),
      // so the host (DocumentLibrary) still owns the side-effect cadence.
      try {
        const { refreshLibraryIndex } = await import('../../services/knowledge/libraryIndex');
        void refreshLibraryIndex(siteId).catch(e =>
          console.warn('[libraryIndex] delete-side refresh failed:', (e as Error)?.message));
      } catch (e) {
        console.warn('[libraryIndex] delete-side refresh skipped:', (e as Error)?.message);
      }
      // Notify the host with the LIVE doc set (soft-deleted rows hidden)
      // so the embedded count chip + downstream consumers stay in sync.
      onDocumentsChange?.(freshDocs().filter(d => isLiveDoc(d)));
      // DDL-FOLLOWUP-CONFIRM-DIALOG — audit emit AFTER the store write so
      // a failed delete doesn't lie in the activity log. Soft-fail.
      try {
        void emitAuditEvent({
          action: 'document.soft_deleted',
          target: {
            type: 'document',
            id: docId,
            label: target?.fileName,
          },
          metadata: {
            siteId,
            originalFileName: target?.fileName,
            referencingReportCount: refCount,
          },
        });
      } catch (e) {
        console.warn('[DDL] soft-delete audit emit failed:', (e as Error)?.message);
      }
      toast.success(`Deleted "${fileName}" — restorable for ${SOFT_DELETE_TTL_DAYS} days from Ghost Docs.`);
      return;
    }
    // Report-mode fallback (legacy `reportId`-only mounts) — preserve the
    // pre-DDL behaviour so report-keyed callers still work. Report mode
    // doesn't have a soft-delete surface (no site to host the Ghost Docs
    // panel), so a hard filter here is the right call.
    const updated = freshDocs().filter(d => d.id !== docId);
    await commit(updated, 'delete');
    toast.success('Document removed');
  };

  /**
   * DLB-1 + DLB-2 — bulk-delete helper. Common path for both
   * "Delete all N noise docs" and "Delete N older revisions". Surfaces
   * a confirm dialog first with a preview of the candidates, then
   * funnels through the store's `bulkRemoveSiteDocuments` (DLB-3).
   *
   * Site-mode only. Report-mode (legacy reportId-only mounts) keeps
   * the per-row trash icon path; bulk-delete is gated by the
   * site-level persistence shape (cross-array sweep + tombstones).
   */
  const handleBulkDelete = useCallback(
    async (candidates: SiteDocument[], reason: 'noise' | 'older-revision', title: string, body: string) => {
      if (!siteId || candidates.length === 0) return;
      // Preview first 10 fileNames in the modal body so the user sees
      // what they're nuking. >10 fold into "… and N more" so a 200-doc
      // bulk doesn't crash the dialog layout.
      const previewCount = 10;
      const names = candidates.map(d => displayFileName(d) || d.fileName);
      const previewLines = names.slice(0, previewCount);
      const overflow = names.length - previewCount;
      const bodyText =
        body +
        '\n\n' +
        previewLines.map(n => '• ' + n).join('\n') +
        (overflow > 0 ? `\n• … and ${overflow} more` : '');
      const ok = await openConfirmDialog({
        title,
        body: bodyText,
        confirmLabel: `Delete ${candidates.length}`,
        cancelLabel: 'Cancel',
        tone: 'danger',
      });
      if (!ok) return;
      const docIds = candidates.map(d => d.id);
      try {
        await bulkRemoveSiteDocuments(siteId, docIds, reason);
        // Library-index refresh — mirrors the single-delete path.
        try {
          void refreshLibraryIndex(siteId).catch(e =>
            console.warn('[libraryIndex] bulk-delete refresh failed:', (e as Error)?.message));
        } catch (e) {
          console.warn('[libraryIndex] bulk-delete refresh skipped:', (e as Error)?.message);
        }
        const after = freshDocs().filter(d => !docIds.includes(d.id));
        onDocumentsChange?.(after);
        toast.success(`Deleted ${docIds.length} document${docIds.length === 1 ? '' : 's'}`);
      } catch (e) {
        console.warn('[DLB] bulk-delete failed:', (e as Error)?.message);
        toast.error('Bulk delete failed — see console.');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [siteId, bulkRemoveSiteDocuments, onDocumentsChange],
  );

  const handleRename = async (docId: string) => {
    if (!editName.trim()) return;
    // DDL-3 — route through the cross-array helper so the new fileName
    // lands on every report.documents copy too. Otherwise the union
    // dedupe would surface the stale name on the next render.
    try {
      await commitFieldUpdate(docId, { fileName: editName.trim() });
      setEditingId(null);
      setEditName('');
      toast.success('Document renamed');
    } catch (error) {
      toast.error(`Document rename was not saved: ${(error as Error).message}`);
    }
  };

  /**
   * UPE-2 / DLS-1 — clicking an extraction-status chip routes to the
   * same KSD5/DLS-1 re-extract path the doc detail drawer / DVF-3
   * pattern uses. Site-mode only — the legacy report-keyed mode has
   * no `site.id` so the path is a no-op there (we keep the button
   * disabled by not rendering the chip as clickable when the action
   * can't succeed; the chip's `isActionableStatus` gates this).
   *
   * Two entry shapes share one handler:
   *   - Retry (failed-* / success-partial) — re-extract the sections
   *     the doc is already linked to. Default (non-force) behaviour.
   *   - DLS-1 "Scan now" (pending) — FORCE extraction even when the doc
   *     is on zero SED sections, so a section-unmatched doc isn't a
   *     dead end. We detect the pending case from the doc's current
   *     status and pass `{ force: true }` so `reExtractDocForSed`
   *     derives candidate sections from the doc's classifier hints and
   *     always stamps a terminal status (out of `pending`).
   */
  const demonstrationRebuild = React.useRef<Promise<void> | null>(null);
  /** Demo extraction receipts are server-owned, like their source stamps.
   * Reuse the normal guarded rebuild rather than a browser document patch. */
  const rebuildDemonstrationEvidence = (documentIds: readonly string[], forceClassification = false): Promise<void> => {
    if (demonstrationRebuild.current) return demonstrationRebuild.current;
    const operation = (async () => {
      const uid = auth.currentUser?.uid;
      const live = useStore.getState().siteRag.sites.find(site => site.id === siteId);
      if (!uid || !siteId || !live?.demonstrationProvenance
        || siteOwnerUid(live, uid) !== uid || live.demonstrationProvenance.ownerUid !== uid
        || documentIds.some(id => !live.documents?.some(document => document.id === id))) {
        throw new Error('Reload the canonical owner document library before rebuilding evidence.');
      }
      // Classification and the generated map commit under their own source CAS
      // before the SED worker freezes its input documents. A failed ACK/readback
      // must not race a rebuild with an unconfirmed library revision.
      const { runSiteDocumentMaintenance } = await import('../../services/siteDocumentMaintenanceClient');
      await runSiteDocumentMaintenance(siteId, 'classify', undefined, forceClassification ? [...documentIds] : undefined);
      const maintained = useStore.getState().siteRag.sites.find(site => site.id === siteId);
      if (auth.currentUser?.uid !== uid || !maintained || siteOwnerUid(maintained, uid) !== uid
        || maintained.demonstrationProvenance?.ownerUid !== uid
        || documentIds.some(id => !maintained.documents?.some(document => document.id === id))) {
        throw new Error('The owner document library changed before rebuilding evidence. Reload to review it.');
      }
      await useStore.getState().rebuildSiteSed(siteId, { force: true });
      // The store action returns void. Confirm its terminal published pair,
      // rather than interpreting a resolved kickoff as successful mapping.
      const { db } = await import('../../firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      if (auth.currentUser?.uid !== uid) throw new Error('The signed-in account changed during the evidence rebuild.');
      const snapshot = await getDoc(doc(db, `users/${uid}/sites`, siteId));
      const current = snapshot.exists() ? snapshot.data() as import('../../types/site').SiteEntity : undefined;
      if (auth.currentUser?.uid !== uid || !current || current.id !== siteId || siteOwnerUid(current, uid) !== uid
        || current.demonstrationProvenance?.ownerUid !== uid
        || !['complete', 'partial'].includes(current.sedStatus ?? '') || !current.sedId || !current.sedStoragePath
        || !Number.isSafeInteger(current.sedVersion) || (current.sedVersion ?? 0) <= (live.sedVersion ?? 0)
        || current.sedContentHashVersion !== current.sedVersion || !/^[a-f0-9]{64}$/.test(current.sedContentHash ?? '')
        || documentIds.some(id => !current.documents?.some(document => document.id === id))) {
        throw new Error('The refreshed SED could not be confirmed. Reload Site evidence to inspect the server outcome.');
      }
    })();
    demonstrationRebuild.current = operation;
    void operation.finally(() => {
      if (demonstrationRebuild.current === operation) demonstrationRebuild.current = null;
    }).catch(() => { /* the calling UI owns the failure message */ });
    return operation;
  };

  const handleReExtractDoc = async (docId: string) => {
    if (!siteId) {
      toast.error('Re-extract requires site-mode (no site context attached).');
      return;
    }
    // DLS-1 — pending docs take the force path. The status is read from
    // the fresh doc list so we don't act on a stale closure.
    const targetDoc = freshDocs().find(d => d.id === docId);
    if (targetDoc?.includeInSed === false) {
      toast.info('This document is excluded from SED — enable "In SED" before scanning.');
      return;
    }
    const isScanNow = (targetDoc?.extractionStatus ?? 'pending') === 'pending';
    // Scans extract INTO the SED — without one, reExtractDocForSed
    // null-no-ops. Say so up front instead of surfacing a generic
    // "Scan failed" (and don't flip the chip for a scan that can't run):
    // the first SED build scans every pending doc anyway.
    const liveSite = useStore.getState().siteRag.sites.find(s => s.id === siteId);
    if (!liveSite?.sedStoragePath) {
      toast.info('No Site Evidence Dossier yet — run the SED build first; it scans every pending document automatically.');
      return;
    }
    if (liveSite.demonstrationProvenance) {
      try { await rebuildDemonstrationEvidence([docId], true); }
      catch (error) { toast.error(`Document remains attached, but evidence rebuild could not be confirmed: ${(error as Error).message}`); }
      return;
    }
    // Optimistic flip — the chip immediately reads as `'extracting'`
    // so the user sees their click did something. The terminal status
    // (`success-*` / `failed-*`) is stamped by the force-mode scan.
    const priorStatus = targetDoc?.extractionStatus ?? 'pending';
    const rollbackExtracting = async () => {
      try {
        await patchSiteDocument(siteId, docId, {
          extractionStatus: priorStatus,
          extractionStatusAt: Date.now(),
          extractionStatusError: targetDoc?.extractionStatusError,
        });
      } catch { /* best-effort — a stale 'extracting' resolves on the next rebuild */ }
    };
    try {
      await patchSiteDocument(siteId, docId, {
        extractionStatus: 'extracting',
        extractionStatusAt: Date.now(),
        extractionStatusError: undefined,
      });
    } catch (e) {
      console.warn('[UPE-2] optimistic status flip failed:', (e as Error)?.message);
    }
    try {
      const { reExtractDocForSed } = await import('../../services/sedBootstrap');
      // Force mode for BOTH entry shapes. For a doc already linked to
      // sections the extraction loop is identical to the legacy retry —
      // force only adds the terminal-status stamp at the end. The
      // non-force path stamps nothing, which left retried docs stuck at
      // 'extracting' until the next full SED rebuild.
      const result = await reExtractDocForSed(siteId, docId, { force: true });
      if (!result) {
        await rollbackExtracting();
        toast.error(isScanNow ? 'Scan failed — see console.' : 'Re-extract failed — see console.');
        return;
      }
      const verb = isScanNow ? 'Scanned' : 'Re-extracted';
      const stamped = result.stampedRequirements?.length ?? 0;
      toast.success(
        result.changedSections.length > 0 || stamped > 0
          ? `${verb} ${result.changedSections.length} section(s)`
            + (stamped > 0 ? ` and extracted fields for ${stamped} requirement(s).` : '.')
          : `${verb} — no SED changes.`,
      );
    } catch (e) {
      await rollbackExtracting();
      toast.error(`${isScanNow ? 'Scan' : 'Re-extract'} failed: ${(e as Error)?.message}`);
    }
  };

  /**
   * DLS-2 — post-upload auto-scan. Freshly uploaded docs used to sit at
   * `'pending'` until the next full SED rebuild (or a manual "Scan now"
   * per doc); now each new doc is routed through the same DLS-1
   * force-extraction path in the background as soon as the upload
   * commits.
   *
   * Deliberately SEQUENTIAL: the extractor is one Gemini call per
   * (doc × candidate section), so a parallel fan-out over a multi-file
   * drop trips the per-minute quota and lands every doc at
   * `'failed-quota'`. Fire-and-forget — per-doc failures stamp their own
   * `failed-*` status via stampForcedScanStatus and never block or fail
   * the upload flow.
   *
   * No-op when the site has no SED yet: there is nothing to extract
   * INTO, and the first SED build scans every pending doc anyway.
   */
  const autoScanUploadedDocs = (docIds: string[]) => {
    if (!siteId || docIds.length === 0) return;
    void (async () => {
      const site = useStore.getState().siteRag.sites.find(s => s.id === siteId);
      if (!site?.sedStoragePath) return;
      if (site.demonstrationProvenance) {
        try { await rebuildDemonstrationEvidence(docIds); }
        catch (error) {
          toast.warning(`Files are attached, but evidence rebuild could not be confirmed: ${(error as Error).message}`, {
            description: 'Reload Site evidence and check the server rebuild before retrying. Do not upload the files again.',
          });
        }
        return;
      }
      const { reExtractDocForSed } = await import('../../services/sedBootstrap');
      for (const docId of docIds) {
        const currentDoc = useStore.getState().siteRag.sites
          .find(s => s.id === siteId)?.documents?.find(d => d.id === docId);
        if (currentDoc?.includeInSed === false) continue;
        try {
          await patchSiteDocument(siteId, docId, {
            extractionStatus: 'extracting',
            extractionStatusAt: Date.now(),
            extractionStatusError: undefined,
          });
          const result = await reExtractDocForSed(siteId, docId, { force: true });
          if (!result) {
            // Early null (SED load blip / doc raced off the site) — put
            // the doc back to 'pending' so the chip isn't stranded at
            // 'extracting' with no scan in flight.
            await patchSiteDocument(siteId, docId, {
              extractionStatus: 'pending',
              extractionStatusAt: Date.now(),
            });
          } else if (
            (result.stampedRequirements?.length ?? 0) > 0
            || result.changedSections.length > 0
          ) {
            // Post-upload visibility — the scan populated evidence; tell the
            // user WHAT landed and hand them the review surface (Accept /
            // Edit / Reject lives on the Site evidence tab + Coverage
            // Matrix requirement detail). The action is a plain URL switch
            // so it works from every mount of the library.
            const docName = useStore.getState().siteRag.sites
              .find(s => s.id === siteId)?.documents?.find(d => d.id === docId)?.fileName ?? 'Upload';
            const stamped = result.stampedRequirements?.length ?? 0;
            toast.success(
              `${docName}: populated ${result.changedSections.length} section(s)`
              + (stamped > 0 ? ` and extracted fields for ${stamped} requirement(s).` : '.'),
              {
                duration: 12_000,
                action: {
                  label: 'Review fields',
                  onClick: () => {
                    window.location.assign(`/sites/${siteId}?tab=evidence`);
                  },
                },
              },
            );
          }
        } catch (e) {
          console.warn(`[DLS-2] auto-scan of doc ${docId} failed:`, (e as Error)?.message);
          try {
            await patchSiteDocument(siteId, docId, {
              extractionStatus: 'pending',
              extractionStatusAt: Date.now(),
            });
          } catch { /* best-effort — next rebuild re-stamps */ }
        }
      }
    })();
  };

  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadFailure, setUploadFailure] = useState<string | null>(null);
  const [savedPickerOpen, setSavedPickerOpen] = useState(false);
  const [savedPickerFiles, setSavedPickerFiles] = useState<SiteDocument[]>([]);
  const [savedPickerLoading, setSavedPickerLoading] = useState(false);
  const [savedAttachmentBusy, setSavedAttachmentBusy] = useState<string | null>(null);
  const attachmentOperations = React.useRef(new Map<string, string>());
  const ownsDemonstration = isSiteMode && !!siteId && !!usageContext.site?.demonstrationProvenance
    && !!auth.currentUser && siteOwnerUid(usageContext.site, auth.currentUser.uid) === auth.currentUser.uid
    && usageContext.site.demonstrationProvenance.ownerUid === auth.currentUser.uid;
  const documentsBasis = (docs: readonly SiteDocument[] = []) => docs.map(siteDocumentMetadataBasisHash).join(',');

  /** Confirm the server write by a fresh canonical read. Only document-owned
   * fields are reconciled, and a newer local document edit is never discarded. */
  const refreshAttachedDocument = async (ownerUid: string, documentId: string, contentHash: string, before: import('../../types/site').SiteEntity) => {
    const { db } = await import('../../firebase');
    const { doc, getDoc } = await import('firebase/firestore');
    if (auth.currentUser?.uid !== ownerUid) throw new Error('The signed-in account changed. Reload to review the attachment.');
    const snapshot = await getDoc(doc(db, `users/${ownerUid}/sites`, siteId!));
    if (!snapshot.exists()) throw new Error('The attachment was saved, but the site could not be reloaded.');
    const persisted = snapshot.data() as import('../../types/site').SiteEntity;
    if (!persisted.documents?.some(document => document.id === documentId && document.contentHash === contentHash)
      || !persisted.documentIds?.includes(documentId)) {
      throw new Error('The attachment was saved, but its canonical document readback is unavailable.');
    }
    const current = useStore.getState().siteRag.sites.find(site => site.id === siteId);
    if (auth.currentUser?.uid !== ownerUid || !current || siteOwnerUid(current, ownerUid) !== ownerUid
      || ![documentsBasis(before.documents), documentsBasis(persisted.documents)].includes(documentsBasis(current.documents))) {
      throw new Error('The attachment was saved, but the account or document library changed. Reload to review the saved file.');
    }
    useStore.setState(state => ({ siteRag: { ...state.siteRag,
      sites: state.siteRag.sites.map(site => site.id === siteId ? { ...site,
        documents: persisted.documents, documentIds: persisted.documentIds,
        ...(persisted.sedReportMaterialHashes && JSON.stringify(site.sedReportMaterialHashes) === JSON.stringify(before.sedReportMaterialHashes)
          ? { sedReportMaterialHashes: persisted.sedReportMaterialHashes } : {}),
        ...(persisted.evidenceMode && site.evidenceMode === before.evidenceMode ? { evidenceMode: persisted.evidenceMode } : {}),
        updatedAt: persisted.updatedAt > site.updatedAt ? persisted.updatedAt : site.updatedAt,
      } : site),
    } }));
    return persisted.documents;
  };

  const attachSavedDocument = async (document: SiteDocument, confirmed: () => void) => {
    const uid = auth.currentUser?.uid;
    const site = useStore.getState().siteRag.sites.find(site => site.id === siteId);
    if (!uid || !siteId || !site?.demonstrationProvenance || siteOwnerUid(site, uid) !== uid
      || site.demonstrationProvenance.ownerUid !== uid) {
      throw new Error('Only the signed-in site owner can attach a saved file to this demonstration.');
    }
    if (!document.contentHash || !/^[0-9a-f]{64}$/i.test(document.contentHash)) {
      throw new Error('This saved file has no valid original-content hash. It cannot be attached here.');
    }
    const { attachSavedSiteDocument } = await import('../../services/siteDocumentAttachmentClient');
    if (auth.currentUser?.uid !== uid) throw new Error('The signed-in account changed. Reload before attaching.');
    const operationKey = JSON.stringify([uid, siteId, document.id, document.contentHash]);
    let operationId = attachmentOperations.current.get(operationKey);
    if (!operationId) {
      operationId = crypto.randomUUID();
      attachmentOperations.current.set(operationKey, operationId);
    }
    const ack = await attachSavedSiteDocument({ siteId, documentId: document.id,
      expectedContentHash: document.contentHash, operationId });
    if (ack.ownerUid !== uid) throw new Error('Site attachment acknowledgment does not match the canonical owner.');
    confirmed();
    return refreshAttachedDocument(uid, document.id, document.contentHash, site);
  };

  const openSavedPicker = async () => {
    if (!ownsDemonstration || savedPickerLoading) return;
    const uid = auth.currentUser!.uid;
    setSavedPickerOpen(true); setSavedPickerLoading(true); setUploadFailure(null);
    try {
      const { loadUserDocuments } = await import('../../services/knowledge/documentUpload');
      const saved = await loadUserDocuments({ strict: true });
      const site = useStore.getState().siteRag.sites.find(site => site.id === siteId);
      if (auth.currentUser?.uid !== uid || !site || siteOwnerUid(site, uid) !== uid) {
        throw new Error('The signed-in account or site changed. Reopen the saved-file picker.');
      }
      const attached = new Set([...(site.documentIds ?? []), ...(site.documents ?? []).map(document => document.id)]);
      setSavedPickerFiles(saved.filter(document => isLiveDoc(document) && !attached.has(document.id)
        && !!document.contentHash && /^[0-9a-f]{64}$/i.test(document.contentHash)));
    } catch (error) { setSavedPickerFiles([]); setUploadFailure((error as Error).message); }
    finally { setSavedPickerLoading(false); }
  };

  const handleSavedAttachment = async (document: SiteDocument) => {
    if (savedAttachmentBusy) return;
    setSavedAttachmentBusy(document.id); setUploadFailure(null);
    let confirmed = false;
    try {
      const documents = await attachSavedDocument(document, () => { confirmed = true; });
      onDocumentsChange?.(documents);
      setSavedPickerFiles(files => files.filter(file => file.id !== document.id));
      toast.success('Saved file attached.');
      if (useStore.getState().siteRag.sites.find(site => site.id === siteId)?.evidenceMode !== 'ai-only') {
        autoScanUploadedDocs([document.id]);
      }
    } catch (error) {
      const message = confirmed ? `File attached, but reload is needed: ${(error as Error).message}`
        : `Attachment was not confirmed. Keep saved file ${document.id}; do not upload again. ${(error as Error).message}`;
      setUploadFailure(message); toast.error(message);
    } finally { setSavedAttachmentBusy(null); }
  };

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploadFailure(null);
    // Demonstration attachments use owner-only server intake; reject a shared
    // operator before processing or saving any new source bytes.
    if (isSiteMode && usageContext.site?.demonstrationProvenance && !ownsDemonstration) {
      const message = 'Only the site owner can upload demonstration documents. No file was uploaded.';
      setUploadFailure(message);
      toast.error(message);
      return;
    }
    setIsUploading(true);
    // UFL-1/2 — classifyUploadFileList partitions valid/rejected with a
    // structured reason for each rejection. Replaces the legacy
    // `No supported documents found` single-toast that named only the
    // wrong-ext case (customer-feedback bug 2026-05-18: a 10.6 MB PDF
    // was bouncing off the 10 MB cap with a toast that lied about its
    // extension being unsupported).
    const allFiles = Array.from(files);
    const { valid: validFiles, rejected } = classifyUploadFileList(allFiles);
    // UFL-5 — one audit event per rejection (best-effort soft-fail).
    for (const r of rejected) {
      emitUploadRejected({
        fileName: r.file.name,
        sizeBytes: r.file.size,
        reason: r.reason.kind,
        surface: 'document-library',
        siteId,
        reportId,
        ext: r.reason.kind === 'unsupported-ext' ? r.reason.ext : undefined,
        capBytes: r.reason.kind === 'too-large' ? r.reason.capBytes : undefined,
      });
    }
    if (validFiles.length === 0) {
      // Every file was rejected — emit one toast per distinct reason
      // (grouped) so the user sees actionable copy for every cause
      // without 100 toasts when a folder of `.DS_Store` is dropped.
      for (const msg of summarizeRejections(rejected)) toast.error(msg);
      setIsUploading(false);
      return;
    }
    if (rejected.length > 0) {
      // Some succeeded, some failed — emit a partial-success toast
      // with details for up to the first 5 rejections so the user
      // knows which files are missing without scrolling a wall of
      // notifications.
      const head = rejected
        .slice(0, 5)
        .map(r => `• ${describeUploadRejection(r.file, r.reason)}`)
        .join('\n');
      const tail = rejected.length > 5 ? `\n…and ${rejected.length - 5} more.` : '';
      toast.warning(
        `Uploading ${validFiles.length} of ${allFiles.length} files. ${rejected.length} skipped:\n${head}${tail}`,
      );
    }
    // DAE4 — the store-for-later interstitial + evidence-pipeline skip
    // fire ONLY on an EXPLICIT 'ai-only' choice, not on the legacy
    // `undefined` fallback. An undefined-mode site auto-promotes to
    // 'docs-included' on this very upload (maybeFlipEvidenceModeOnDocUpload),
    // so gating it through isAiOnlyEvidenceMode() showed a dialog whose
    // "stored for later, not used" promise the promote rule immediately
    // broke.
    const aiOnlyUpload = isSiteMode && usageContext.site?.evidenceMode === 'ai-only';
    if (aiOnlyUpload) {
      const { openConfirmDialog } = await import('../ui/confirmDialog');
      const ok = await openConfirmDialog({
        title: 'Store document for later?',
        body: 'This site runs on the Datacrate Engine only. Your file will be saved to the library but will not be used in the SED or reports until you switch to Docs included.',
        confirmLabel: 'Store anyway',
        cancelLabel: 'Cancel',
      });
      if (!ok) {
        setIsUploading(false);
        return;
      }
    }
    setUploadProgress(`0/${validFiles.length}`);
    // Persistent toast — survives unmount so the user sees progress
    // even after closing the library modal or switching reports/sites.
    // Unique id per upload batch so concurrent uploads don't collide.
    const toastId = `doc-upload-${Date.now()}`;
    toast.loading(`Uploading 0 / ${validFiles.length} documents…`, { id: toastId });
    const savedDocumentIds: string[] = [];
    const confirmedDocumentIds: string[] = [];
    const failedFiles: { fileName: string; error: string; retryable: boolean }[] = [];
    let attachmentConfirmed = false;
    try {
      const { processUploadedFile, saveDocument } = await (await import('../../utils/safeImport')).safeImport(() => import('../../services/knowledge/documentUpload'));
      const { processUploadedFileBundle } = await import('../../services/knowledge/documentBundle');
      const newDocs: SiteDocument[] = [];
      // Process one at a time to avoid memory crashes
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress(`${i + 1}/${validFiles.length}`);
        toast.loading(`Uploading ${i + 1} / ${validFiles.length} — ${file.name}`, { id: toastId });
        try {
          // URE-2 / CMU-2 — thread the upload-time intent. Three modes:
          //   (a) Replace mode (`replaceTarget` non-null): the user
          //       clicked Coverage Matrix `[Replace]`. The new doc
          //       inherits the OLD doc's requirementId via the URE-2
          //       intent plumbing AND the URL-carried requirementId so
          //       the row-fill logic stays coherent across the version
          //       boundary.
          //   (b) Upload-for mode (`focusUploadForId`): missing-row
          //       upload OR add-supporting-doc — same URE-2 plumbing.
          //   (c) Free drop — no intent.
          const uploadOpts = {
            ...(replaceTarget
              ? {
                  intendedRequirementIds: [replaceTarget.requirementId],
                  intentSource: 'coverage-matrix-replace-button' as const,
                }
              : focusUploadForId
              ? {
                  intendedRequirementIds: [focusUploadForId],
                  intentSource: 'coverage-matrix-upload-button' as const,
                }
              : {
                  intentSource: 'free-drop' as const,
                }),
            siteId: siteId ?? usageContext.site?.id,
            skipEvidencePipeline: aiOnlyUpload,
            onStageChange: (stage: 'extracting' | 'summarizing' | 'indexing' | 'classifying' | 'persisting') => {
              const stageLabel =
                stage === 'extracting'
                  ? 'extracting text'
                  : stage === 'summarizing'
                    ? 'summarizing'
                    : stage === 'indexing'
                      ? 'indexing topics'
                      : stage === 'classifying'
                        ? 'classifying relevance'
                        : 'saving original bytes';
              setUploadProgress(`${i + 1}/${validFiles.length} (${stageLabel})`);
              toast.loading(`Uploading ${i + 1} / ${validFiles.length} — ${file.name} (${stageLabel})`, {
                id: toastId,
              });
            },
          };
          const bundle = await processUploadedFileBundle(file, uploadOpts, processUploadedFile);
          for (const { doc, file: documentFile } of bundle) {
            const relativePath = (file as any).webkitRelativePath || file.name;
            if (!doc.parentDocId && relativePath !== file.name) doc.fileName = relativePath;
            // CMU-2 — stamp the version-chain back-pointer on the new
            // doc when we're in replace mode. The old doc gets soft-
            // archived in the post-upload commit phase below (after
            // we know the new doc id landed).
            if (replaceTarget && !doc.parentDocId) {
              doc.replacesDocId = replaceTarget.docId;
            }
            // Require durable original-byte retention before claiming upload
            // success or pushing the document into `newDocs`: never mistake
            // extracted text for a preserved original.
            setUploadProgress(`${i + 1}/${validFiles.length} (saving original bytes)`);
            toast.loading(`Uploading ${i + 1} / ${validFiles.length} — ${doc.fileName || file.name} (saving original bytes)`, {
              id: toastId,
            });
            // #89 — pass the owning site id (same resolution as the
            // upload-opts above) so the DOCUMENT write is gated by that
            // site's edit-lock (assertSiteWritable).
            const saveResult = await saveDocument(
              doc,
              documentFile,
              siteId ?? usageContext.site?.id,
              {
                requireDurableOriginalBytes: true,
                uploadId: `chunk-${doc.id}`,
                abortSessionOnError: false,
              },
            );
            if (saveResult?.rawUploadWarning) {
              const rawErr = new Error(saveResult.rawUploadWarning.message) as Error & {
                retryable?: boolean;
              };
              rawErr.retryable = saveResult.rawUploadWarning.kind !== 'too-large';
              throw rawErr;
            }
            savedDocumentIds.push(doc.id);
            newDocs.push(doc);
            for (const attachment of doc.emailAttachments ?? []) {
              if (attachment.status !== 'ingested') toast.warning(`${attachment.fileName}: ${attachment.reason}`);
            }
          }
        } catch (fileErr) {
          console.error(`[DocLibrary] Failed: ${file.name}`, fileErr);
          if (usageContext.site?.demonstrationProvenance && isSiteMode) {
            throw fileErr;
          }
          const detail = fileErr instanceof Error ? fileErr.message : String(fileErr);
          const retryable = (fileErr as { retryable?: boolean })?.retryable !== false;
          failedFiles.push({ fileName: file.name, error: detail, retryable });
        }
      }
      if (newDocs.length > 0) {
        const currentDocs = freshDocs();
        const existingIds = new Set(currentDocs.map(d => d.id));
        const trulyNew = newDocs.filter(d => !existingIds.has(d.id));
        // DDL-5 — append-only write. Pre-DDL we pushed
        //   [...currentDocs (union), ...trulyNew]
        // back through commit() → onUpdateDocuments → updateSiteDocuments,
        // which re-wrote the entire union (including every report-derived
        // doc) into `site.documents`. That made every report-included doc
        // un-deletable AND grew `site.documents` monotonically. The store
        // action `appendSiteDocuments` now writes only the new docs onto
        // the canonical site array; the union continues to display
        // report-derived rows but they no longer infiltrate the site
        // doc on every upload.
        if (siteId && onUpdateDocuments) {
          if (usageContext.site?.demonstrationProvenance) {
            for (const document of trulyNew) {
              await attachSavedDocument(document, () => { confirmedDocumentIds.push(document.id); });
            }
          } else {
            await appendSiteDocuments(siteId, trulyNew);
          }
          attachmentConfirmed = true;
          // Library-index refresh — same debounced cadence the legacy
          // commit('upload') path used.
          try {
            const { refreshLibraryIndexDebounced } = await import('../../services/knowledge/libraryIndex');
            if (!usageContext.site?.demonstrationProvenance) refreshLibraryIndexDebounced(siteId);
          } catch (e) {
            console.warn('[libraryIndex] upload-side refresh skipped:', (e as Error)?.message);
          }
          // CAF-5 — post-upload coverage reconcile. The fresh docs may
          // satisfy previously-missing requirements; we re-resolve the
          // full Coverage Matrix so the CAF-4 chip + the resolver
          // envelope on `site.coverage[reqId]` reflect the new state.
          // Fire-and-forget — never blocks the upload toast / dialog
          // close. Failures inside the writer DON'T regress the user-
          // visible upload success.
          try {
            const { writeSiteCoverage } = await import('../../services/cafService');
            void writeSiteCoverage(siteId).catch((e: unknown) =>
              console.warn(
                '[DocumentLibrary] CAF coverage writer failed:',
                (e as Error)?.message,
              ),
            );
          } catch (e) {
            console.warn(
              '[DocumentLibrary] CAF coverage writer skipped:',
              (e as Error)?.message,
            );
          }
          onDocumentsChange?.(freshDocs());
          // DLS-2 — background auto-scan of the new docs. Skipped for
          // AI-only sites (docs are stored but not evidence-eligible)
          // and for synthetic library-index rows.
          if (!aiOnlyUpload) {
            autoScanUploadedDocs(trulyNew.filter(d => !isLibraryIndexDoc(d) && d.includeInSed !== false).map(d => d.id));
          }
        } else {
          // Legacy non-site-scoped path (report-mode, or site-mode without
          // a siteId prop). Keep the prior shape so report-keyed callers
          // continue to work.
          const updated = [...currentDocs, ...trulyNew];
          await commit(updated, 'upload');
          attachmentConfirmed = true;
        }

        // CMU-2 — replace-mode post-upload: soft-archive the OLD doc
        // (the one being replaced) and clear the URL focus param. We
        // run this AFTER the new doc has been persisted so the version
        // chain is well-formed by the time the archive flag lands —
        // the dedupe helpers + Documents-tab filters can rely on
        // `replacesDocId → archived` always being a valid pointer.
        //
        // Soft-archive only: no hard delete. Reports + SED lineage
        // already on the old docId stay valid (same audit-trail
        // invariant as `unresolvedConflicts` snapshots — see SC2).
        if (replaceTarget && trulyNew.length > 0) {
          const newDocId = trulyNew[0]!.id;
          try {
            await commitFieldUpdate(replaceTarget.docId, {
              archived: true,
              archivedAt: Date.now(),
              archivedReason: `replaced-by-${newDocId}`,
            });
            toast.success(
              `Replaced ${replaceTarget.doc?.fileName ?? 'document'}. The old version is archived.`,
            );
          } catch (e) {
            console.warn('[DocLibrary] soft-archive of replaced doc skipped:', (e as Error)?.message);
            // Don't surface as an error toast — the new doc landed
            // fine and the old one will be hidden by the canonical-
            // name dedupe regardless; this is belt-and-braces.
          }
          // Clear the URL focus param so a reload doesn't re-prime
          // replace mode (the upload already committed).
          onFocusHandled?.();
        }

        // Cross-site doc reuse detector (Documents_On_Site Prompt 7-C):
        // when an uploaded doc's contentHash matches a doc on another
        // site this user has access to, surface the duplicate so they
        // can decide whether to share rather than maintain two copies.
        if (isSiteMode && (siteId || trulyNew.some(d => d.id.startsWith('library-index-')))) {
          try {
            const { findCrossSiteDuplicates } = await import('../../services/knowledge/crossSiteDocReuse');
            const allSites = useStore.getState().siteRag.sites;
            const ownerSiteId = siteId
              ?? trulyNew.find(d => d.id.startsWith('library-index-'))?.id.replace(/^library-index-/, '')
              ?? '';
            for (const newDoc of trulyNew) {
              const matches = findCrossSiteDuplicates(newDoc, ownerSiteId, allSites);
              if (matches.length === 0) continue;
              const otherSiteName = matches[0]!.site.name;
              const extra = matches.length > 1 ? ` and ${matches.length - 1} other${matches.length === 2 ? '' : 's'}` : '';
              toast.message(
                `"${newDoc.fileName}" already lives on ${otherSiteName}${extra}.`,
                { description: 'You may want to share rather than keep duplicates across sites.' },
              );
            }
          } catch (e) {
            console.warn('[DocLibrary] cross-site duplicate scan skipped:', (e as Error)?.message);
          }
        }
      }
      if (!attachmentConfirmed) {
        if (failedFiles.length > 0) {
          throw new Error(
            failedFiles
              .map(f => `${f.fileName} (${f.retryable ? 'retryable' : 'terminal'}): ${f.error}`)
              .join('; '),
          );
        }
        throw new Error('No document attachment was completed.');
      }
      if (failedFiles.length > 0) {
        const failureSummary = failedFiles
          .map(f => `${f.fileName} (${f.retryable ? 'retryable' : 'terminal'}: ${f.error})`)
          .join('; ');
        const partialMessage = `${newDocs.length} of ${validFiles.length} document(s) added; ${failedFiles.length} failed durable original-byte storage and must be retried: ${failureSummary}`;
        setUploadFailure(partialMessage);
        toast.warning(partialMessage, { id: toastId });
      } else {
        toast.success(`${newDocs.length} document(s) added to library${newDocs.some(d => d.parentDocId) ? ' (including email attachments)' : ''}`, { id: toastId });
        const deferredDocs = newDocs.filter(d => d.enrichmentStatus === 'deferred');
        if (deferredDocs.length > 0) {
          toast.warning(
            `${deferredDocs.length} document(s) saved with verified original bytes; optional summarization/indexing was deferred due to timeout.`,
          );
        }
      }
    } catch (e) {
      console.error('[DocLibrary] Upload failed:', e);
      const detail = (e as Error).message;
      const message = attachmentConfirmed
        ? `Documents attached, but a follow-up step failed: ${detail}`
        : confirmedDocumentIds.length
          ? `Files attached: ${confirmedDocumentIds.join(', ')}. Further attachment or reload failed. Do not upload again. ${detail}`
        : savedDocumentIds.length
          ? `Files saved, but site attachment was not confirmed. Do not upload again. Saved document IDs: ${savedDocumentIds.join(', ')}. ${detail}`
          : `Upload failed: ${detail}`;
      setUploadFailure(message);
      toast.error(message, { id: toastId });
    } finally {
      setIsUploading(false);
      setUploadProgress('');
    }
  };

  if (!open && !embedded) return null;

  const triggerFileUpload = () => {
    document.querySelector<HTMLInputElement>('[data-doc-library-upload-files]')?.click();
  };
  const triggerFolderUpload = () => {
    document.querySelector<HTMLInputElement>('[data-doc-library-upload-folder]')?.click();
  };
  const savedFileRecovery = ownsDemonstration ? (
    <div className="mx-4 mt-3 space-y-2">
      <Button variant="outline" disabled={savedPickerLoading || !!savedAttachmentBusy || isUploading}
        onClick={() => { void openSavedPicker(); }}>Attach saved file</Button>
      {savedPickerOpen && <div className="rounded-md border border-border p-3 space-y-2" data-testid="doc-library-saved-picker">
        <div className="flex justify-between gap-3"><span className="type-body">Your saved files not attached to this site</span>
          <button type="button" disabled={!!savedAttachmentBusy} onClick={() => setSavedPickerOpen(false)}>Close</button></div>
        <p className="type-meta text-text-muted">Uses the original saved file. No upload or source classification is repeated.</p>
        {savedPickerLoading ? <p role="status">Loading saved files…</p>
          : savedPickerFiles.length === 0 ? <p className="type-body">No eligible saved files found.</p>
            : <div className="max-h-40 overflow-y-auto space-y-1">{savedPickerFiles.map(document => (
              <div key={document.id} className="flex justify-between items-center gap-3">
                <span className="type-body truncate" title={document.id}>{document.fileName}</span>
                <Button size="sm" disabled={!!savedAttachmentBusy} onClick={() => { void handleSavedAttachment(document); }}>
                  {savedAttachmentBusy === document.id ? 'Attaching…' : 'Attach'}
                </Button>
              </div>
            ))}</div>}
      </div>}
    </div>
  ) : null;

  /** Embedded site tab — first load with no docs: landing card only (matches Site Evidence). */
  if (embedded && documents.length === 0) {
    return (
      <div className="flex flex-col h-full min-w-0 w-full overflow-hidden">
        <input
          data-doc-library-upload-files=""
          type="file"
          multiple
          accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.md,.json,.rtf,.pptx,.ppt,.jpg,.jpeg,.png,.gif,.kmz,.kml,.eml,.msg,.url,.log,.xml,.html"
          className="hidden"
          onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
        />
        <input
          data-doc-library-upload-folder=""
          type="file"
          className="hidden"
          {...{ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>}
          onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
        />
        <DocumentsUploadLandingCard
          recommendedReportTypes={recommendedReportTypes}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          onUploadFiles={triggerFileUpload}
          onUploadFolder={triggerFolderUpload}
          onSkip={onSkipUploads}
        />
        {savedFileRecovery}
        {uploadFailure && (
          <div role="alert" className="mx-4 mt-3 border border-warning px-3 py-2 type-body text-text-primary" data-testid="doc-library-upload-failure">
            {uploadFailure}
          </div>
        )}
      </div>
    );
  }

  // Body is shell-agnostic. The two outer shells live at module scope
  // (`DocLibraryEmbeddedShell` / `DocLibraryModalShell`) so their element
  // type is stable across renders — see the comment on those components.
  // Defining the shell inline here would remount this whole subtree on
  // every keystroke and blur the search input.
  const body = (
    <>
      {/* Founder QA r3 item 5a — EVIDENCE MODE leads the Documents tab:
          the condensed Aurora segmented control (selected-accent active
          state, ⓘ-tooltip explainer) renders above everything else so
          the SED/report source decision is the first thing on the tab.
          Site-mode only — legacy report-mode mounts have no site. */}
      {isSiteMode && usageContext.site ? (
        <div className="px-4 pt-3 shrink-0" data-testid="doc-library-evidence-mode-row">
          <EvidenceModeControl site={usageContext.site} condensed />
        </div>
      ) : null}

      {/* Header strip — MODAL mode only (icon + title + totals + close).
          Item 5b: in EMBEDDED mode (the site Documents tab) this strip is
          gone — the on-disk size chip and every control button (sort,
          view toggle, admin export) now live on the FILTER row, so the
          tab opens straight into evidence mode + upload + filters. */}
      {!embedded && (
        <div
          className="px-4 py-3 border-b border-border flex items-center gap-3 shrink-0"
          data-testid="doc-library-header"
        >
          <FolderOpen className="w-5 h-5 text-primary shrink-0" />
          <h2 className="text-base font-bold uppercase tracking-wider text-foreground">
            Document Library
          </h2>
          <span className="text-xs text-text-muted tabular-nums" data-testid="doc-library-stats">
            · {documents.length} files · {formatSize(totalBytes)}
          </span>
          <button
            onClick={onClose}
            className="ml-auto p-1 text-text-secondary hover:text-text-primary"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hidden file input — kept around so the drop zone, the empty
          state, and any other affordance can fire it via .click()
          without re-implementing the upload pipeline. */}
      <input
        data-doc-library-upload-files=""
        type="file" multiple accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls,.md,.json,.rtf,.pptx,.ppt,.jpg,.jpeg,.png,.gif,.kmz,.kml,.eml,.msg,.url,.log,.xml,.html"
        className="hidden"
        onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
      />

      {uploadFailure && (
        <div role="alert" className="mx-4 mt-3 border border-warning px-3 py-2 type-body text-text-primary" data-testid="doc-library-upload-failure">
          {uploadFailure}
        </div>
      )}
      {/* CMU-2 — replace-mode banner. Surfaces the doc being replaced
          + a Cancel escape so the user can back out of replace mode
          without uploading. Lives above the dropzone so it's visually
          adjacent to the affordance it modifies; the highlight ring
          from the receiver still lands on the dropzone itself. */}
      {replaceTarget && (
        <div
          className="mx-4 mt-3 rounded-lg border border-warning bg-warning/10 px-3 py-2 flex items-center gap-2 text-[11px] text-text-primary"
          data-testid="doc-library-replace-banner"
          data-replace-doc-id={replaceTarget.docId}
          data-replace-requirement-id={replaceTarget.requirementId}
        >
          <RefreshCw className="w-3.5 h-3.5 text-warning shrink-0" aria-hidden="true" />
          <div className="flex-1 leading-snug">
            <span className="font-semibold">Replace mode.</span>
            {' '}
            The next upload will replace{' '}
            <span className="font-mono">
              {replaceTarget.doc?.fileName ?? replaceTarget.docId}
            </span>
            . The old version is archived (not deleted) and stays
            available in the audit trail.
          </div>
          <button
            type="button"
            onClick={() => { onFocusHandled?.(); }}
            className="type-body uppercase tracking-widest text-text-secondary hover:underline shrink-0"
            data-testid="doc-library-replace-cancel"
          >
            Cancel replace
          </button>
        </div>
      )}

      {/* FIX 3 + founder QA r3 item 3 — ONE compact upload row. The
          dashed drag-target strip and the folder-upload affordance share
          a single slim h-9 row (plus the From-Library toggle in legacy
          report-mode) instead of stacking a second dashed toolbar row —
          reclaims ~50px of vertical space. The drop target still expands
          to 64px while files are dragged over the page (`isDraggingFiles`)
          or while a replace is staged. Same `doc-library-dropzone` testid
          + click-to-browse + drop wiring; same hidden
          `[data-doc-library-upload-folder]` input. */}
      <div className="mx-4 mt-3 flex items-stretch gap-2" data-testid="doc-library-upload-row">
        <div
          className={`flex-1 min-w-0 rounded-md border-2 border-dashed flex items-center justify-center gap-2 cursor-pointer transition-all ${
            replaceTarget || isDraggingFiles ? 'h-16' : 'h-9'
          } ${
            replaceTarget
              ? 'border-warning bg-warning/10 text-warning'
              : isDraggingFiles
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-state-hover/40 text-text-muted hover:bg-state-hover'
          }`}
          data-testid="doc-library-dropzone"
          data-replace-mode={replaceTarget ? 'true' : undefined}
          data-collapsed={!replaceTarget && !isDraggingFiles ? 'true' : undefined}
          onClick={() => {
            const input = document.querySelector<HTMLInputElement>('[data-doc-library-upload-files]');
            input?.click();
          }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingFiles(false);
            const files = e.dataTransfer?.files;
            if (files?.length) void handleUpload(files);
          }}
        >
          {!replaceTarget && !isDraggingFiles && !isUploading ? (
            <Plus className="w-4 h-4 text-text-muted" />
          ) : (
            <UploadCloud className={`w-5 h-5 ${replaceTarget ? 'text-warning' : isDraggingFiles ? 'text-primary' : 'text-text-muted'}`} />
          )}
          <span className="text-[11px] font-semibold uppercase tracking-widest">
            {isUploading
              ? `Uploading ${uploadProgress}…`
              : replaceTarget
                ? `Drop new version of ${replaceTarget.doc?.fileName ?? 'document'}`
                : isDraggingFiles
                  ? 'Drop files to upload'
                  : 'Upload or drag files'}
          </span>
        </div>
        <label className="flex shrink-0 cursor-pointer">
          <input
            data-doc-library-upload-folder=""
            type="file" className="hidden" {...{ webkitdirectory: '', directory: '' } as any}
            onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
          />
          <span
            className="rounded-md border border-dashed border-border px-3 flex items-center justify-center gap-1 hover:bg-state-hover type-body uppercase tracking-widest text-text-muted transition-colors"
            data-testid="doc-library-upload-folder-button"
            title="Upload an entire folder — its subfolder structure becomes folder tags"
          >
            <FolderOpen className="w-3 h-3" /> Upload folder
          </span>
        </label>
        {!isSiteMode && reportId && (
          <button
            onClick={() => setShowLibraryPicker(!showLibraryPicker)}
            className="rounded-md border border-dashed border-primary/40 px-3 cursor-pointer hover:bg-primary/10 type-body uppercase tracking-widest text-primary flex items-center justify-center gap-1 shrink-0">
            <Plus className="w-3 h-3" /> From Library
          </button>
        )}
      </div>
      {/* UFL-3 — surface the cap + supported set under the dropzone so
          the user doesn't have to discover the limit by failing. Demoted to
          the explainer-hints surface (only shown while dragging or replacing,
          or when a file picker is imminent) so the steady-state strip stays
          slim per FIX 3; the testid is preserved for the upload-limit guard. */}
      <div
        className={`mx-4 mt-1 type-meta ${replaceTarget || isDraggingFiles ? '' : 'sr-only'}`}
        data-testid="doc-library-dropzone-hint"
      >
        Max {Math.round(UPLOAD_MAX_FILE_BYTES / 1024 / 1024)} MB per file · PDF, DOCX, XLSX, PPTX, TXT/MD/CSV, images (JPG/PNG/…), KMZ, EML, MP4
      </div>
      {savedFileRecovery}

        {/* Library picker — only available in report-mode (scans other reports of the same site). */}
        {showLibraryPicker && reportId && (() => {
          const rid = reportId;
          const report = useStore.getState().siteRag.reports.find(r => r.id === rid);
          const site = siteForReport(useStore.getState().siteRag.sites, report);
          const existingIds = new Set(documents.map(d => d.id));
          const libraryDocs: SiteDocument[] = [];
          if (site) {
            for (const r of useStore.getState().siteRag.reports.filter(r2 =>
              r2.id !== rid && reportBelongsToSite(site, r2)
            )) {
              for (const d of r.documents || []) {
                if (!existingIds.has(d.id)) { existingIds.add(d.id); libraryDocs.push(d); }
              }
            }
          }
          return (
            <div className="mx-4 mt-2 rounded-md border border-primary/30 bg-primary/5 p-2 space-y-1">
              <div className="flex items-center justify-between">
                <span className="type-meta font-bold text-primary">Add from Document Library ({libraryDocs.length} available)</span>
                <button onClick={() => setShowLibraryPicker(false)} className="type-meta text-text-secondary hover:text-text-primary">Close</button>
              </div>
              {libraryDocs.length === 0 ? (
                <div className="type-body text-text-muted py-2">All library documents are already included in this report</div>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-0.5">
                  {libraryDocs.map(d => (
                    <button key={d.id} className="w-full text-left type-body text-text-muted hover:bg-state-hover rounded-md px-2 py-1 flex items-center gap-2"
                      onClick={async () => {
                        const cur = freshDocs();
                        if (cur.some(fd => fd.id === d.id)) { toast.info('Already added'); return; }
                        const updated = [...cur, d];
                        await commit(updated);
                        toast.success(`Added "${d.fileName}" to report documents`);
                      }}>
                      <FileText className="w-3 h-3 text-primary shrink-0" />
                      <span className="truncate flex-1">{d.fileName}</span>
                      <span className="text-text-secondary shrink-0 tabular-nums">{(d.sizeBytes / 1024).toFixed(0)}KB</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* DTC-1 (2026-05-18) — consolidated filter bar. Before this
             pack the same surface stacked 6 sub-rows that consumed
             349px vertical: HIDE NOISE/GROUP REVISIONS toggles, search
             + 7 type pills, 55 raw classifier tag pills, 18 huge SED-
             contribution pills (5 rows), 6 APPLICABLE TO pills, count.
             Now it's a single ~80px bar — search + 2 toggles +
             "Filters ▾" Popover trigger — with an active-chip row
             below it that only renders when filters are active.
             URL/state shape preserved (`?filter[contrib]`, `?filter[usedin]`,
             `?focus=req:<id>`). All `data-testid`s preserved (the popover
             scopes the type / tag / contrib / usedin pills but the IDs
             stay stable so tests open the popover first). */}
        {totalNonIndexCount > 0 && (
          <div
            className="px-4 pt-3 flex flex-col gap-2"
            data-testid="doc-library-filter-row"
          >
            {/* Site Evidence mode (ES3) moved to the TOP of the tab
                (item 5a) — see the condensed EvidenceModeControl row at
                the head of the body. */}
            {/* Primary bar — single row, ~40px tall. */}
            <div
              className="flex flex-wrap items-center gap-2"
              data-testid="doc-library-filter-bar"
            >
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search
                  className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search name, summary, tags…"
                  className="w-full text-xs pl-7 pr-2 py-1 rounded-md border border-border bg-background focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                  aria-label="Search documents"
                  data-testid="doc-library-filter-search"
                />
              </div>
              {/* FIX 3 — the hide-noise / group-revisions toggles + the
                   bulk-delete (noise / older revisions) affordances moved
                   INTO the "Filters ▾" popover (see PopoverContent below) so
                   the primary bar is just search + Filters ▾. The
                   `doc-library-noise-row` wrapper + every toggle/bulk testid
                   are preserved verbatim — they now live behind the popover
                   trigger. */}
              <Popover open={filtersPopoverOpen} onOpenChange={setFiltersPopoverOpen}>
                <PopoverTrigger
                  data-testid="doc-library-filter-popover-trigger"
                  aria-pressed={filtersPopoverOpen}
                  className={`type-meta px-2 py-1 rounded-md border transition-colors flex items-center gap-1 ${
                    advancedFilterCount > 0
                      ? 'border-primary text-primary bg-primary/10'
                      : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                  }`}
                >
                  Filters
                  {advancedFilterCount > 0 && (
                    <span
                      className="ml-1 px-1 py-0.5 type-body bg-primary text-primary-foreground rounded tabular-nums"
                      data-testid="doc-library-filter-popover-badge"
                    >
                      {advancedFilterCount}
                    </span>
                  )}
                  <span aria-hidden="true">▾</span>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="w-[min(560px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto"
                  data-testid="doc-library-filter-popover-content"
                >
                  {/* FIX 3 — hide-noise / group-revisions toggles + bulk-delete
                       (noise / older revisions). Relocated here from the
                       primary filter bar so the steady-state surface is just
                       search + Filters ▾. Wrapper + all testids preserved. */}
                  <div className="space-y-1">
                    <div className="type-body uppercase tracking-widest text-text-muted">
                      Display
                    </div>
                    <div
                      className="flex items-center gap-2 flex-wrap"
                      data-testid="doc-library-noise-row"
                    >
                      <button
                        type="button"
                        onClick={() => setHideNoise(v => !v)}
                        aria-pressed={hideNoise}
                        className={`type-meta px-1.5 py-0.5 rounded-md border transition-colors ${
                          hideNoise
                            ? 'border-primary text-primary bg-primary/10'
                            : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                        }`}
                        title="Hide z. Archive folders, .url shortcuts, .eml files, 0-byte placeholders, and Office temp lock files. Toggle to see everything."
                        data-testid="doc-library-hide-noise-toggle"
                      >
                        {hideNoise ? '✓ Hide noise' : 'Hide noise'}
                        {noiseHiddenCount > 0 && (
                          <> ({noiseHiddenCount} hidden)</>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setGroupRevisions(v => !v)}
                        aria-pressed={groupRevisions}
                        className={`type-meta px-1.5 py-0.5 rounded-md border transition-colors ${
                          groupRevisions
                            ? 'border-primary text-primary bg-primary/10'
                            : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                        }`}
                        title="Collapse documents that share the same contentHash into one canonical row (latest upload wins). Toggle off for a flat list of every revision."
                        data-testid="doc-library-group-revisions-toggle"
                      >
                        {groupRevisions ? '✓ Group revisions' : 'Group revisions'}
                        {revisionGroupedCount > 0 && (
                          <> ({revisionGroupedCount} grouped)</>
                        )}
                      </button>
                      {/* DLB-1 — bulk-delete noise affordance (site-mode only,
                           when noise is hidden + at least one doc is hidden). */}
                      {!!siteId && isSiteMode && hideNoise && noiseHiddenCount > 0 && (
                        <button
                          type="button"
                          data-testid="doc-library-bulk-delete-noise"
                          onClick={() => {
                            const noiseDocs = documents.filter(
                              d => !isLibraryIndexDoc(d) && isNoisyDoc(d),
                            );
                            void handleBulkDelete(
                              noiseDocs,
                              'noise',
                              `Delete ${noiseDocs.length} noise documents?`,
                              `z. Archive folders, .url shortcuts, .eml files, 0-byte placeholders, and Office temp lock files. These ${noiseDocs.length} docs are currently hidden by the noise filter and won't influence any report.`,
                            );
                          }}
                          className="type-meta px-1.5 py-0.5 rounded-md border border-danger/40 text-danger hover:bg-danger/10 transition-colors"
                          title="Delete every document currently hidden by the noise filter. Tombstoned so a re-upload of the same file id is blocked for 30 days."
                        >
                          🗑 Delete {noiseHiddenCount} noise
                        </button>
                      )}
                      {/* DLB-2 — bulk-delete older revisions (keeps each
                           group's canonical/newest member). */}
                      {!!siteId && isSiteMode && groupRevisions && revisionGroupedCount > 0 && (
                        <button
                          type="button"
                          data-testid="doc-library-bulk-delete-older-revisions"
                          onClick={() => {
                            const olderRevDocs: SiteDocument[] = [];
                            for (const grp of revisionGroupsByCanonical.values()) {
                              const allMembers = [grp.canonical, ...grp.earlier];
                              const newest = pickNewestRevision(allMembers);
                              for (const d of allMembers) {
                                if (d.id !== newest.id) olderRevDocs.push(d);
                              }
                            }
                            void handleBulkDelete(
                              olderRevDocs,
                              'older-revision',
                              `Delete ${olderRevDocs.length} older revisions?`,
                              `For each revision group, the newest version stays. ${olderRevDocs.length} older versions across ${revisionGroupsByCanonical.size} group${revisionGroupsByCanonical.size === 1 ? '' : 's'} will be removed.`,
                            );
                          }}
                          className="type-meta px-1.5 py-0.5 rounded-md border border-danger/40 text-danger hover:bg-danger/10 transition-colors"
                          title="Delete every older revision (same contentHash, earlier uploadedAt). Keeps the canonical/newest member of each group."
                        >
                          🗑 Delete {revisionGroupedCount} older
                        </button>
                      )}
                      <span
                        className="hidden type-body text-text-muted"
                        data-testid="doc-library-noise-row-summary"
                        aria-hidden="true"
                      >
                        Showing {visibleNonIndexCount} of {totalNonIndexCount}
                        {hideNoise && noiseHiddenCount > 0 && (
                          <> ({noiseHiddenCount} noise hidden</>
                        )}
                        {hideNoise && noiseHiddenCount > 0 && groupRevisions && revisionGroupedCount > 0 && (
                          <>, {revisionGroupedCount} revisions grouped</>
                        )}
                        {(!hideNoise || noiseHiddenCount === 0) && groupRevisions && revisionGroupedCount > 0 && (
                          <> ({revisionGroupedCount} revisions grouped</>
                        )}
                        {((hideNoise && noiseHiddenCount > 0) || (groupRevisions && revisionGroupedCount > 0)) && (
                          <>)</>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* File type pills — 7 options. */}
                  <div className="space-y-1">
                    <div className="type-body uppercase tracking-widest text-text-muted">
                      File type
                    </div>
                    <div className="flex items-center gap-1 flex-wrap" data-testid="doc-library-filter-types">
                      {SET_D2_TYPE_OPTIONS.map(opt => {
                        const active = typeFilter.has(opt.value);
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setTypeFilter(prev => {
                                const next = new Set(prev);
                                if (next.has(opt.value)) next.delete(opt.value);
                                else next.add(opt.value);
                                return next;
                              });
                            }}
                            aria-pressed={active}
                            className={`type-meta px-1.5 py-0.5 rounded-md border transition-colors ${
                              active
                                ? 'border-primary text-primary bg-primary/10'
                                : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                            }`}
                            data-testid={`doc-library-filter-type-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* SED contribution pills — DTC-4 surfaces per-pill
                       doc counts and sorts by count desc so high-signal
                       categories float to the top. Zero-count pills
                       still render (dimmed) so the user can plan an
                       upload from the popover. */}
                  <div className="space-y-1">
                    <div className="type-body uppercase tracking-widest text-text-muted">
                      SED contribution
                    </div>
                    <div
                      className="flex items-center gap-1 flex-wrap"
                      data-testid="doc-library-contrib-pills"
                    >
                      {contribOptionsWithCount.map(item => {
                        const active = contribFilter.has(item.id);
                        const zero = item.count === 0;
                        return (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => toggleContrib(item.id)}
                            aria-pressed={active}
                            title={item.description}
                            className={`type-meta px-1.5 py-0.5 rounded-md border transition-colors ${
                              active
                                ? 'border-primary text-primary bg-primary/10'
                                : `border-border ${zero ? 'opacity-50' : ''} text-text-muted hover:text-primary hover:border-primary/50`
                            }`}
                            data-testid={`doc-library-contrib-pill-${item.id}`}
                          >
                            {item.label}
                            <span className="ml-1 type-body opacity-60">{item.count}</span>
                            {active && <span className="ml-1">×</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* APPLICABLE-TO pills — DRT-2 semantics (applicable
                       report types), DTC-4 per-pill counts. URL key
                       `filter[usedin]` preserved for back-compat. */}
                  <div className="space-y-1">
                    <div className="type-body uppercase tracking-widest text-text-muted">
                      Applicable to
                    </div>
                    <div
                      className="flex items-center gap-1 flex-wrap"
                      data-testid="doc-library-usedin-pills"
                    >
                      {usedInOptionsWithCount.map(t => {
                        const active = usedInFilter.has(t.id);
                        const zero = t.count === 0;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => toggleUsedIn(t.id)}
                            aria-pressed={active}
                            title={t.label}
                            className={`type-meta px-1.5 py-0.5 rounded-md border transition-colors ${
                              active
                                ? 'border-primary text-primary bg-primary/10'
                                : `border-border ${zero ? 'opacity-50' : ''} text-text-muted hover:text-primary hover:border-primary/50`
                            }`}
                            data-testid={`doc-library-usedin-pill-${t.id}`}
                          >
                            {t.shortLabel}
                            <span className="ml-1 type-body opacity-60">{t.count}</span>
                            {active && <span className="ml-1">×</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <label className="flex items-center gap-1 type-meta text-text-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={inSedOnly}
                      onChange={e => setInSedOnly(e.target.checked)}
                      className="accent-primary"
                      data-testid="doc-library-filter-in-sed-only"
                    />
                    In SED only
                  </label>

                  {/* DTC-2 — bury the 55-tag raw classifier dump
                       behind a collapsed <details>. Default closed;
                       expand to see the alphabetized tag grid. The
                       inline note flags that these tags overlap with
                       the SED contribution categories above — that's
                       why they're demoted here rather than removed. */}
                  {availableTags.length > 0 && (
                    <details className="mt-1">
                      <summary className="type-body uppercase tracking-widest text-text-muted cursor-pointer select-none">
                        All tags ({availableTags.length})
                      </summary>
                      <p className="type-body text-text-muted mt-1 mb-1.5">
                        These tags come from automated classification and may overlap with the SED contribution categories above. Use SED contribution for high-signal filtering; tags for niche matching.
                      </p>
                      <div
                        className="flex items-center gap-1 flex-wrap"
                        data-testid="doc-library-filter-tags"
                      >
                        {availableTags.map(tag => {
                          const active = tagFilter.has(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => {
                                setTagFilter(prev => {
                                  const next = new Set(prev);
                                  if (next.has(tag)) next.delete(tag);
                                  else next.add(tag);
                                  return next;
                                });
                              }}
                              aria-pressed={active}
                              className={`text-[10px] px-1.5 py-0.5 rounded-md border transition-colors ${
                                active
                                  ? 'border-primary text-primary bg-primary/10'
                                  : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                              }`}
                              data-testid={`doc-library-filter-tag-${tag}`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </details>
                  )}
                </PopoverContent>
              </Popover>
              {/* Item 5b — library controls consolidated onto the filter
                   row (the embedded header strip is gone): sort, the
                   List/Cards view toggle, the admin-only audit export and
                   (embedded) the on-disk size chip. All testids preserved
                   verbatim from the old header strip. */}
              <div className="ml-auto flex items-center gap-2 flex-wrap justify-end min-w-0 shrink">
              {/* DAT-5 — sort affordance. Opt-in importance sort exposes
                    the doc-first ranking; Recent (default) preserves the
                    historical folder/newest-first cadence. */}
              <label className="flex items-center gap-1 type-meta text-text-muted">
                Sort
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value as SortMode)}
                  className="type-meta border border-border px-1 py-0.5 rounded-md bg-background"
                  data-testid="doc-library-sort-mode"
                >
                  <option value="recent">Recent</option>
                  <option value="name">Name</option>
                  <option value="importance">Importance ↓</option>
                </select>
              </label>
              {/* DAT-FOLLOWUP-EXPORT-DOC-AUDIT — admin-only CSV export.
                    Renders only for users holding the admin claim (probed
                    once on mount via `requireAdminClaim`). The export is
                    scoped to the docs currently in this library + the
                    tombstones for the same site so the file is a
                    site-coherent audit snapshot. */}
              {isAdmin && siteId && (
                <Button
                  size="sm"
                  variant="outline"
                  // Documents-tab declutter (2026-06-11): the host header's
                  // "Export audit" proxy button is gone, so this admin-only
                  // button is the ONLY audit-export entry point in the app and
                  // stays visible in embedded mode too (it was sr-only while
                  // the host header carried the visible duplicate — FIX 3).
                  className="h-8 type-meta"
                  data-testid="doc-library-export-audit"
                  onClick={async () => {
                    const site = usageContext.site;
                    const siteName = site?.name ?? siteId ?? '';
                    try {
                      // Lazy-load: keep the audit module out of the main
                      // doc-library bundle for the non-admin path.
                      const [{ buildDocAuditCsv }, tombMod] = await Promise.all([
                        import('../../services/docAuditExport'),
                        import('../../services/knowledge/documentUpload'),
                      ]);
                      let tombstones: Awaited<ReturnType<typeof tombMod.listDocTombstones>> = [];
                      try {
                        tombstones = await tombMod.listDocTombstones();
                      } catch (e) {
                        // Tombstone read failure shouldn't block the live-doc
                        // export — log + continue with an empty tombstone set.
                        console.debug('[doc-audit] tombstone read failed:', (e as Error)?.message);
                      }
                      const csv = buildDocAuditCsv({
                        siteId: siteId!,
                        siteName,
                        documents: documents.filter(d => !isLibraryIndexDoc(d)),
                        reports: usageContext.reports,
                        tombstones,
                      });
                      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                      a.href = url;
                      a.download = `doc-audit-${siteName.replace(/[^a-zA-Z0-9-_]+/g, '_')}-${stamp}.csv`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success(`Exported ${documents.filter(d => !isLibraryIndexDoc(d)).length} docs to CSV`);
                    } catch (e) {
                      toast.error(`Export failed: ${(e as Error).message}`);
                    }
                  }}
                >
                  Export doc audit (CSV)
                </Button>
              )}
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'default' : 'outline'}
                className="h-8 type-meta"
                onClick={() => setViewMode('list')}
                data-testid="doc-library-view-list"
              >
                <ListIcon className="w-3.5 h-3.5 mr-1" /> List
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'cards' ? 'default' : 'outline'}
                className="h-8 type-meta"
                onClick={() => setViewMode('cards')}
                data-testid="doc-library-view-cards"
              >
                <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Cards
              </Button>
                {embedded && (
                  <span
                    className="type-meta text-text-muted tabular-nums"
                    data-testid="doc-library-header"
                  >
                    <span data-testid="doc-library-stats">{formatSize(totalBytes)}</span>
                  </span>
                )}
              </div>
            </div>

            {/* DTC-1 — active-filter chip row. Only renders when at
                 least one filter is active. Each chip is removable
                 (`× per filter`), and a single "Clear all" button
                 wired to the shared `clearAllFilters` helper sits
                 alongside the count. */}
            {filterActive ? (
              <div
                className="flex items-center gap-1.5 flex-wrap"
                data-testid="doc-library-active-filters"
              >
                <span className="type-body uppercase tracking-widest text-text-muted">
                  Active:
                </span>
                {search.trim() && (
                  <ChipRemovable
                    label={`Search: "${search}"`}
                    onRemove={() => setSearch('')}
                    testId="doc-library-active-chip-search"
                  />
                )}
                {[...typeFilter].map(value => {
                  const opt = SET_D2_TYPE_OPTIONS.find(o => o.value === value);
                  return (
                    <ChipRemovable
                      key={`type-${value}`}
                      label={`Type: ${opt?.label ?? value}`}
                      onRemove={() => setTypeFilter(prev => {
                        const next = new Set(prev);
                        next.delete(value);
                        return next;
                      })}
                      testId={`doc-library-active-chip-type-${value}`}
                    />
                  );
                })}
                {[...contribFilter].map(id => {
                  const opt = contribOptionsWithCount.find(o => o.id === id);
                  return (
                    <ChipRemovable
                      key={`contrib-${id}`}
                      label={opt?.label ?? id}
                      onRemove={() => setContribFilter(prev => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                      })}
                      testId={`doc-library-active-chip-contrib-${id}`}
                    />
                  );
                })}
                {[...usedInFilter].map(id => {
                  const opt = usedInOptionsWithCount.find(o => o.id === id);
                  return (
                    <ChipRemovable
                      key={`usedin-${id}`}
                      label={opt?.shortLabel ?? id}
                      onRemove={() => setUsedInFilter(prev => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                      })}
                      testId={`doc-library-active-chip-usedin-${id}`}
                    />
                  );
                })}
                {[...tagFilter].map(tag => (
                  <ChipRemovable
                    key={`tag-${tag}`}
                    label={`Tag: ${tag}`}
                    onRemove={() => setTagFilter(prev => {
                      const next = new Set(prev);
                      next.delete(tag);
                      return next;
                    })}
                    testId={`doc-library-active-chip-tag-${tag}`}
                  />
                ))}
                {inSedOnly && (
                  <ChipRemovable
                    label="In SED only"
                    onRemove={() => setInSedOnly(false)}
                    testId="doc-library-active-chip-in-sed-only"
                  />
                )}
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="type-meta text-text-muted hover:text-primary underline-offset-2 hover:underline ml-1"
                  data-testid="doc-library-filter-clear"
                >
                  Clear all
                </button>
                <span
                  className="ml-auto type-meta"
                  data-testid="doc-library-filter-count"
                >
                  Showing {visibleNonIndexCount} of {totalNonIndexCount} documents
                  {hideNoise && noiseHiddenCount > 0 && (
                    <> · {noiseHiddenCount} noise hidden</>
                  )}
                  {groupRevisions && revisionGroupedCount > 0 && (
                    <> · {revisionGroupedCount} revisions grouped</>
                  )}
                </span>
              </div>
            ) : (
              <div
                className="type-meta"
                data-testid="doc-library-filter-count"
              >
                Showing {visibleNonIndexCount} of {totalNonIndexCount} documents
                {hideNoise && noiseHiddenCount > 0 && (
                  <> · {noiseHiddenCount} noise hidden</>
                )}
                {groupRevisions && revisionGroupedCount > 0 && (
                  <> · {revisionGroupedCount} revisions grouped</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Item 5d + 5e — FOLDER TABS. One tab per (post-filter) folder
            plus "All". Clicking narrows both layouts to that folder;
            dragging a document CARD over a folder tab shows a drop ring
            and dropping MOVES the doc there (moveSiteDocument — the
            owner-path-safe cross-array action). OS file drags carry the
            'Files' dataTransfer type, not DOC_CARD_DRAG_TYPE, so they
            fall through to the upload dropzone untouched. Hidden when
            grouping collapses to a single bucket (importance sort or a
            one-folder library) — a lone tab is noise. Rendered OUTSIDE
            the scroller so tabs stay visible drop targets mid-scroll. */}
        {totalNonIndexCount > 0 && sortMode !== 'importance' && folders.length > 1 && (
          <div
            className="px-4 pt-2 flex items-center gap-1.5 flex-wrap shrink-0"
            data-testid="doc-library-folder-tabs"
            role="tablist"
            aria-label="Document folders"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeFolderTab == null}
              onClick={() => setActiveFolderTab(null)}
              data-testid="doc-library-folder-tab-all"
              className={`rounded-md border px-2 py-1 type-meta uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
                activeFolderTab == null
                  ? 'selected-accent'
                  : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
              }`}
            >
              All
              <span className="tabular-nums opacity-70">{visibleNonIndexCount}</span>
            </button>
            {folders.map(([folderName, fdocs]) => {
              const active = activeFolderTab === folderName;
              const dragOver = dragOverFolderTab === folderName;
              return (
                <button
                  key={folderName}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveFolderTab(active ? null : folderName)}
                  data-testid={`doc-library-folder-tab-${folderName}`}
                  data-drop-active={dragOver ? 'true' : undefined}
                  className={`rounded-md border px-2 py-1 type-meta uppercase tracking-widest transition-colors flex items-center gap-1.5 ${
                    active
                      ? 'selected-accent'
                      : dragOver
                        ? 'border-primary text-primary bg-primary/10 ring-2 ring-primary/40'
                        : 'border-border text-text-muted hover:text-primary hover:border-primary/50'
                  }`}
                  onDragOver={(e) => {
                    // Only document-card drags are valid drop payloads —
                    // OS file drags ('Files') must keep bubbling to the
                    // upload flow, so we don't preventDefault for them.
                    if (Array.from(e.dataTransfer?.types ?? []).includes(DOC_CARD_DRAG_TYPE)) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragOverFolderTab(folderName);
                    }
                  }}
                  onDragLeave={() => {
                    setDragOverFolderTab(prev => (prev === folderName ? null : prev));
                  }}
                  onDrop={(e) => {
                    const docId = e.dataTransfer?.getData(DOC_CARD_DRAG_TYPE);
                    setDragOverFolderTab(null);
                    if (!docId) return; // not a card drag (e.g. OS files)
                    e.preventDefault();
                    e.stopPropagation();
                    void handleDocDropOnFolder(folderName, docId);
                  }}
                >
                  <FolderOpen className="w-3 h-3" aria-hidden />
                  {folderName}
                  <span className="tabular-nums opacity-70">{fdocs.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Folder tree */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* New Folder button */}
          <div className="mb-2">
            {showNewFolder ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <FolderOpen className="w-3.5 h-3.5 text-warning shrink-0" />
                <input type="text" value={customFolderName} onChange={e => setCustomFolderName(e.target.value)}
                  placeholder="Folder name" className="type-body flex-1 px-1.5 py-0.5 rounded-md border border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1" autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && customFolderName.trim()) { setCustomFolders(prev => [...prev, customFolderName.trim()]); setShowNewFolder(false); setExpandedFolders(prev => new Set([...prev, customFolderName.trim()])); toast.success(`Folder "${customFolderName.trim()}" created — use ⋮ on documents to move them here`); setCustomFolderName(''); } }} />
                <button onClick={() => { if (customFolderName.trim()) { setShowNewFolder(false); setCustomFolderName(''); toast.success(`Folder created`); } }} className="text-success hover:text-success"><Check className="w-3 h-3" /></button>
                <button onClick={() => setShowNewFolder(false)} className="text-text-secondary hover:text-text-primary"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <button onClick={() => setShowNewFolder(true)} className="type-body text-text-muted hover:text-primary flex items-center gap-1 px-2">
                <FolderOpen className="w-3 h-3" /> + New Folder
              </button>
            )}
          </div>

          {/* Founder QA r3 item 4 — the "Library Map (auto-generated)"
              pinned card is GONE. The map itself still exists (the
              library-index doc keeps feeding `--- LIBRARY MAP ---` into
              report prompts) but it's pure infrastructure now: every
              document mutation refreshes it automatically — uploads via
              the debounced `refreshLibraryIndexDebounced` in commit(),
              edits/renames/moves/deletes via commit()/commitFieldUpdate()/
              handleDelete()/handleBulkDelete(), folder renames below, and
              admin restores in GhostDocsPanel. No user-facing card, no
              manual refresh button. */}

          {documents.length === 0 ? (
            <DocumentLibraryEmptyState
              embedded={!!embedded}
              recommendedReportTypes={recommendedReportTypes}
              onUpload={(folder) => {
                // Trigger the appropriate hidden input above the empty state
                // by id selector — keeps the rich CTA from re-implementing
                // the upload pipeline. Looks for the right input within
                // this DocumentLibrary instance via a stable test id.
                const input = document.querySelector<HTMLInputElement>(
                  folder ? '[data-doc-library-upload-folder]' : '[data-doc-library-upload-files]',
                );
                input?.click();
              }}
              // SET-D2 — when filters narrowed all docs out the rich
              // empty state would mislead ("no docs uploaded"). Forced
              // via render branch below instead.
              onSkip={onSkipUploads}
            />
          ) : visibleNonIndexCount === 0 && filterActive ? (
            /* DTC-3 — the empty-state clear button uses the shared
                 `clearAllFilters` helper (resets all six filter types AND
                 strips the `?filter[…]` URL params). Reskinned onto the
                 Aurora-Cockpit shared EmptyState. */
            <EmptyState
              data-testid="doc-library-filter-empty"
              icon={SearchX}
              title="No documents match the current filters"
              body={
                advancedFilterCount > 0
                  ? `${advancedFilterCount} filter${advancedFilterCount === 1 ? '' : 's'} active${search.trim() ? ' plus a search query' : ''}.`
                  : 'Try widening the category, tag, or search to see more of the library.'
              }
              action={(
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="da-btn"
                  data-testid="doc-library-filter-empty-clear"
                >
                  Clear all filters
                </button>
              )}
            />
          ) : viewMode === 'cards' ? (
            <div
              className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
              data-testid="doc-library-cards"
            >
              {visibleFolders.flatMap(([folderName, fdocs]) => fdocs.map(doc => {
                const ext = doc.fileName.split('.').pop()?.toLowerCase() || '';
                const Icon = getDocIcon(ext);
                return (
                  <div
                    key={doc.id}
                    className="rounded-md border border-border bg-background hover:bg-muted p-3 flex flex-col gap-2 cursor-pointer transition-colors"
                    data-doc-card
                    data-testid={`doc-card-${doc.id}`}
                    onClick={() => setViewerDoc(doc)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setViewerDoc(doc);
                      }
                    }}
                    // Item 5e — drag a card onto a folder tab to move it.
                    // The custom MIME (no 'Files' entry) keeps the upload
                    // dropzone + window drag listener inert for card drags.
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(DOC_CARD_DRAG_TYPE, doc.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragEnd={() => setDragOverFolderTab(null)}
                  >
                    <div className="flex items-start gap-2">
                      <Icon className={`w-6 h-6 shrink-0 ${FILE_ICON_COLOR[ext] ?? 'text-text-secondary'}`} />
                      <div className="flex-1 min-w-0">
                        {/* EDX-7 — leaf filename + folder breadcrumb chip.
                             Replaces the legacy `{doc.fileName}` path-as-title
                             render. Hover surfaces the full path so power
                             users can still see exactly where the doc lives. */}
                        {(() => {
                          const split = displayFileName(doc);
                          const fullPath = split.breadcrumb.length > 0
                            ? `${breadcrumbString(split.breadcrumb)} / ${split.leaf}`
                            : split.leaf;
                          return (
                            <>
                              <div
                                className="text-xs font-semibold text-foreground line-clamp-2 break-words"
                                title={fullPath}
                                data-testid={`doc-card-${doc.id}-name`}
                              >
                                {split.leaf}
                              </div>
                              {doc.attachedFromEmail && <div className="type-body text-text-muted truncate" title={doc.attachedFromEmail.fileName}>Attachment from {doc.attachedFromEmail.subject || doc.attachedFromEmail.fileName}</div>}
                              {split.breadcrumb.length > 0 && (
                                <div
                                  className="type-body text-text-muted truncate mt-0.5"
                                  title={fullPath}
                                  data-testid={`doc-card-${doc.id}-breadcrumb`}
                                >
                                  {breadcrumbString(split.breadcrumb)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="text-[10px] text-text-secondary tabular-nums">
                      {formatSize(doc.sizeBytes)} · {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>
                    {/* UPE-2 / DLS-1 — extraction status chip on the card view, in
                         parity with the row view. Failed / partial chips are clickable
                         (Retry); 'pending' chips are clickable (Scan now). */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <DocRowStatusChip
                        doc={doc}
                        onRetry={(retryDocId) => { void handleReExtractDoc(retryDocId); }}
                      />
                      <DocRowTruncationChip doc={doc} />
                    </div>
                    <div className="type-body uppercase tracking-widest text-text-muted">{folderName}</div>
                    {doc.tags && doc.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {doc.tags.slice(0, 3).map(t => (
                          <span key={t} className="type-body px-1.5 py-0.5 rounded-md border border-border text-text-muted">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Action row — stop propagation so clicks here don't open the viewer. */}
                    <div
                      className="flex items-center gap-1 mt-auto pt-2 border-t border-border-subtle"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={async () => {
                          try {
                            const { downloadDocument } = await import('../../lib/documentDownload');
                            await downloadDocument(doc, binaryLoader);
                          } catch (e) {
                            toast.error((e as Error).message || 'Download failed');
                          }
                        }}
                        className="p-1.5 text-text-secondary hover:text-primary disabled:opacity-30 disabled:hover:text-text-secondary disabled:cursor-not-allowed"
                        disabled={!doc.rawStoragePath}
                        title={doc.rawStoragePath ? 'Download' : 'Re-upload to enable download'}
                        data-testid={`doc-card-download-${doc.id}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-1.5 text-text-secondary hover:text-danger"
                        title="Delete"
                        data-testid={`doc-card-delete-${doc.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {/* SET-B1 — Documents → Site Evidence bridge. Only
                           rendered when the parent provides the handler
                           (omitted in legacy / report-only mounts that
                           don't have a Site Evidence destination). */}
                      {onJumpToEvidence && (
                        <button
                          onClick={() => onJumpToEvidence(doc.id)}
                          className="p-1.5 text-text-secondary hover:text-primary ml-auto"
                          title="View SED contributions for this document"
                          data-testid={`doc-card-jump-evidence-${doc.id}`}
                          aria-label={`View SED contributions for ${doc.fileName}`}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => setDetailDocId(doc.id)}
                        className={`p-1.5 text-text-secondary hover:text-primary ${onJumpToEvidence ? '' : 'ml-auto'}`}
                        title="Show details"
                        data-testid={`doc-card-detail-${doc.id}`}
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              }))}
            </div>
          ) : (
            <div className="space-y-1">
              {visibleFolders.map(([folderName, docs]) => {
                const isOpen = expandedFolders.has(folderName);
                return (
                  <div key={folderName}>
                    {renamingFolder === folderName ? (
                      <div className="flex items-center gap-3 px-2 py-2">
                        <FolderOpen className="w-4 h-4 text-warning shrink-0" />
                        <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                          className="text-xs flex-1 px-2 py-1 rounded-md border border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1" autoFocus
                          onKeyDown={async e => {
                            if (e.key === 'Enter' && newFolderName.trim()) {
                              // DF-1 — cross-array atomic rename. In site-mode we
                              // route through the store's `renameFolder` action so
                              // the new folder lands on `site.documents[i]` AND
                              // every `report.documents[i]` on this site (same
                              // shape as DDL-1's `removeSiteDocument`). In legacy
                              // report-mode we keep the original
                              // freshDocs+commit shape since reports here own
                              // their own `documents` array.
                              const trimmedName = newFolderName.trim();
                              if (siteId) {
                                await renameFolder(siteId, folderName, trimmedName);
                                // QA r3 item 4 — the Library-Map card (and its
                                // manual refresh button) is gone, so every
                                // mutation must refresh the auto-generated map
                                // itself. Debounced: a rename burst coalesces.
                                try {
                                  const { refreshLibraryIndexDebounced } = await import('../../services/knowledge/libraryIndex');
                                  refreshLibraryIndexDebounced(siteId);
                                } catch (err) {
                                  console.warn('[libraryIndex] folder-rename refresh skipped:', (err as Error)?.message);
                                }
                              } else {
                                const updated = freshDocs().map(d =>
                                  categorize(d) === folderName || d.folder === folderName
                                    ? { ...d, folder: trimmedName }
                                    : d,
                                );
                                await commit(updated);
                              }
                              setRenamingFolder(null);
                              toast.success(`Folder renamed to "${trimmedName}"`);
                            }
                          }} />
                        <button onClick={() => setRenamingFolder(null)} className="p-1.5 text-text-secondary hover:text-text-primary" title="Cancel"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                    <div className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted group/folder">
                      <button className="flex items-center gap-2 flex-1 text-left" onClick={() => toggleFolder(folderName)}>
                        {isOpen ? <ChevronDown className="w-4 h-4 text-text-secondary" /> : <ChevronRight className="w-4 h-4 text-text-secondary" />}
                        <FolderOpen className="w-4 h-4 text-warning" />
                        <span className="text-xs font-bold uppercase tracking-wider text-text-muted">{folderName}</span>
                        <span className="text-[10px] text-text-secondary ml-auto tabular-nums">{docs.length}</span>
                      </button>
                      <button onClick={() => { setRenamingFolder(folderName); setNewFolderName(folderName); }}
                        className="p-1.5 text-text-secondary hover:text-primary transition-colors" title="Rename folder">
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                    )}
                    {isOpen && (
                      <div className="ml-5 border-l border-border pl-2 space-y-0.5">
                        {docs.map(doc => {
                          const ext = doc.fileName.split('.').pop()?.toLowerCase() || '';
                          const isEditing = editingId === doc.id;
                          return (
                            <div
                              key={doc.id}
                              className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted group cursor-pointer"
                              data-testid={`doc-row-${doc.id}`}
                              onClick={() => { if (!isEditing) setViewerDoc(doc); }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (isEditing) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setViewerDoc(doc);
                                }
                              }}
                            >
                              {(() => {
                                const Icon = getDocIcon(ext);
                                return <Icon className={`w-4 h-4 shrink-0 ${FILE_ICON_COLOR[ext] ?? 'text-text-secondary'}`} />;
                              })()}
                              <div className="flex-1 min-w-0">
                                {isEditing ? (
                                  <div
                                    className="flex items-center gap-2"
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="text" value={editName} onChange={e => setEditName(e.target.value)}
                                      className="text-xs flex-1 px-2 py-1 rounded-md border border-primary/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                                      autoFocus onKeyDown={e => e.key === 'Enter' && handleRename(doc.id)}
                                    />
                                    <button onClick={() => handleRename(doc.id)} className="p-1.5 text-success hover:text-success" title="Save"><Check className="w-4 h-4" /></button>
                                    <button onClick={() => setEditingId(null)} className="p-1.5 text-text-secondary hover:text-text-primary" title="Cancel"><X className="w-4 h-4" /></button>
                                  </div>
                                ) : (
                                  <>
                                    {/* EDX-7 — leaf filename + folder breadcrumb chip.
                                         Replaces the legacy `{doc.fileName}` path-as-title render.
                                         Folder breadcrumb sits on its own muted line below; the
                                         full path is exposed via `title` and surfaces on hover. */}
                                    {(() => {
                                      const split = displayFileName(doc);
                                      const fullPath = split.breadcrumb.length > 0
                                        ? `${breadcrumbString(split.breadcrumb)} / ${split.leaf}`
                                        : split.leaf;
                                      return (
                                        <div
                                          className="text-xs text-foreground truncate flex items-center gap-1.5"
                                          data-testid={`doc-row-${doc.id}-name`}
                                          title={fullPath}
                                        >
                                          {/* DAT-5 — importance rank badge. Only rendered when
                                               sortMode === 'importance' so the historical row
                                               layout stays identical for the 95% of users on
                                               the default Recent sort. */}
                                          {sortMode === 'importance' && (() => {
                                            const r = importanceRankByDocId.get(doc.id);
                                            if (!r) return null;
                                            return (
                                              <span
                                                className="type-body uppercase tracking-widest rounded border border-primary/40 text-primary bg-primary/5 px-1 py-0.5 shrink-0 font-mono"
                                                title={`Importance index ${r.importance.toFixed(2)} — rank ${r.rank} of ${importanceRankByDocId.size}`}
                                                data-testid={`doc-row-${doc.id}-importance-rank`}
                                              >
                                                #{r.rank}
                                              </span>
                                            );
                                          })()}
                                          <span className="truncate">{split.leaf}</span>
                                          {doc.attachedFromEmail && <span className="type-meta text-text-muted truncate" title={doc.attachedFromEmail.fileName}>Attachment from {doc.attachedFromEmail.subject || doc.attachedFromEmail.fileName}</span>}
                                          {split.breadcrumb.length > 0 && (
                                            <span
                                              className="type-body text-text-muted truncate shrink-0 hidden md:inline"
                                              data-testid={`doc-row-${doc.id}-breadcrumb`}
                                              title={fullPath}
                                            >
                                              · {breadcrumbString(split.breadcrumb)}
                                            </span>
                                          )}
                                          {/* EDX-4 — earlier-revisions expander. Renders only on canonical rows
                                               of a contentHash group that the user has not yet expanded. */}
                                      {groupRevisions && revisionGroupsByCanonical.has(doc.id) && (() => {
                                        const grp = revisionGroupsByCanonical.get(doc.id);
                                        if (!grp) return null;
                                        const isExpanded = expandedRevisionGroups.has(doc.id);
                                        return (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleRevisionGroup(doc.id);
                                            }}
                                            className="type-body uppercase tracking-widest rounded-md border border-border text-text-muted hover:text-primary hover:border-primary px-1.5 py-0.5 shrink-0"
                                            data-testid={`doc-row-${doc.id}-revisions-toggle`}
                                            aria-expanded={isExpanded}
                                            title={isExpanded
                                              ? 'Hide earlier revisions of this document'
                                              : `Show ${grp.earlier.length} earlier revision${grp.earlier.length === 1 ? '' : 's'} of this document (grouped by content hash)`}
                                          >
                                            {isExpanded ? `▾ ${grp.earlier.length} hidden revisions` : `+ ${grp.earlier.length} earlier revisions`}
                                          </button>
                                        );
                                      })()}
                                      {/* EDX-4 — earlier-revisions get a small "older revision" tag so the
                                           reader knows these aren't independent docs. */}
                                      {groupRevisions && swallowedRevisionIds.has(doc.id) && (
                                        <span
                                          className="type-meta rounded border border-border-subtle px-1 py-0.5 shrink-0"
                                          data-testid={`doc-row-${doc.id}-older-revision-tag`}
                                          title="Earlier revision of a canonical document above"
                                        >
                                          older revision
                                        </span>
                                      )}
                                      {/* UPE-2 / DLS-1 — per-doc extraction status chip. Reads
                                           SiteDocument.extractionStatus (UPE-1 field); pre-UPE
                                           docs render as 'pending'. Failed / partial variants are
                                           clickable (Retry) and 'pending' is clickable (Scan now,
                                           force extraction) — both fire `handleReExtractDoc` →
                                           `reExtractDocForSed(siteId, docId, { force? })`, the
                                           DVF-3 / DLS-1 path. */}
                                      <DocRowStatusChip
                                        doc={doc}
                                        onRetry={(retryDocId) => { void handleReExtractDoc(retryDocId); }}
                                      />
                                      <DocRowTruncationChip doc={doc} />
                                      {aiOnlyLibrary && !isLibraryIndexDoc(doc) && (
                                        <span
                                          className="type-meta rounded border border-border-subtle px-1 py-0.5 shrink-0"
                                          data-testid={`doc-row-${doc.id}-stored-not-in-evidence`}
                                          title="Stored for later — switch to Docs included to use in evidence"
                                        >
                                          stored — not in evidence
                                        </span>
                                      )}
                                    </div>
                                      );
                                    })()}
                                    <div className="text-[10px] text-text-secondary tabular-nums">
                                      {formatSize(doc.sizeBytes)} · {new Date(doc.uploadedAt).toLocaleDateString()}
                                      {doc.tags?.length > 0 && ` · ${doc.tags.slice(0, 3).join(', ')}`}
                                    </div>
                                    {/* UXS-7 — "Applicable to" pill group removed.
                                         Live-prod Redhead 2026-05-18 feedback: users
                                         only want to see "Cited by" badges that are
                                         ACTUALLY cited in the report body. The
                                         classifier-driven "Applicable to" set was
                                         duplicative noise on the row; the same
                                         information remains accessible via DAT's
                                         `<DocApplicabilityChips>` `[Show detail]`
                                         button below (DocLineageInspector). */}
                                    {/* UXS-7 — "Cited by" row now reads from
                                         `bodyCitedInReportTypes` (RCO-1 +
                                         CSE-derived `report.claims[]`), not from
                                         `documentRefs.userIncluded`. The new
                                         ground-truth source means: if a doc was
                                         toggled on in the wizard but the narrative
                                         never cited it, the row is hidden — exactly
                                         matching the user request "I only want to
                                         see cited-by badges and it must really be
                                         cited in the doc/report". HARD RULE 10 —
                                         CSE-derived `claims[]` is the canonical
                                         truth set; Path A (`[Ref N]` superscripts)
                                         is the legacy fallback for reports
                                         pre-CSE. SED isn't loaded in this surface,
                                         so Path A doesn't fire here — but the
                                         CSE-claims path is sufficient for fresh
                                         reports.
                                         Testid stays `doc-cited-by-${docId}` so
                                         existing E2E selectors still find the
                                         row. */}
                                    {(() => {
                                      if (!usageContext.site) return null;
                                      const citedBy = bodyCitedInReportTypes(
                                        doc.id,
                                        usageContext.reports,
                                        // sed undefined: this surface doesn't
                                        // load the live SED. Path A is skipped;
                                        // Path B (CSE-derived claims) is
                                        // sufficient for the cited-by signal.
                                        undefined,
                                      );
                                      if (citedBy.length === 0) return null;
                                      return (
                                        <div
                                          className="flex items-center gap-1 flex-wrap mt-1"
                                          data-testid={`doc-cited-by-${doc.id}`}
                                        >
                                          <span className="type-meta uppercase tracking-wider">Cited by:</span>
                                          {citedBy.map(t => {
                                            const meta = REPORT_TYPE_REGISTRY[t as ReportTypeId];
                                            return (
                                              <span
                                                key={t}
                                                className="type-meta px-1.5 py-0.5 rounded border border-warning/40 text-warning bg-warning/5"
                                                title={`${meta?.label ?? t} report's narrative cites this document.`}
                                                data-testid={`doc-cited-by-${doc.id}-${t}`}
                                              >
                                                {meta?.shortLabel ?? t}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      );
                                    })()}
                                    {/* DAT-1 — doc-first applicability chips.
                                         "Populates" SED variable count + "Show detail"
                                         button that opens the DocLineageInspector
                                         (DAT-2/3). Renders nothing when the doc has
                                         zero populated slots and no detail handler. */}
                                    <DocApplicabilityChips
                                      doc={doc}
                                      onShowDetail={(docId) => setLineageDocId(docId)}
                                    />
                                  </>
                                )}
                              </div>
                              {!isEditing && (
                                <div
                                  className="flex gap-1 items-center shrink-0 flex-wrap justify-end"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={async () => {
                                      const before = !!doc.priority;
                                      // DDL-3 — cross-array field write.
                                      try {
                                        await commitFieldUpdate(doc.id, { priority: !before });
                                        const { emitDocumentPriorityToggle } = await import('../../lib/auditLogClient');
                                        const ownerSiteId = siteId
                                          ?? freshDocs().find(d => d.id.startsWith('library-index-'))?.id.replace(/^library-index-/, '');
                                        if (ownerSiteId) emitDocumentPriorityToggle(ownerSiteId, doc.id, doc.fileName, before, !before);
                                      } catch (error) {
                                        toast.error(`Document priority was not saved: ${(error as Error).message}`);
                                      }
                                    }}
                                    className={`p-1.5 transition-colors ${doc.priority ? 'text-warning' : 'text-text-secondary hover:text-warning'}`}
                                    title={doc.priority ? 'Priority (deep analysis) — click to remove' : 'Mark as priority for deeper AI analysis'}
                                  >
                                    <span className="text-base leading-none">{doc.priority ? '★' : '☆'}</span>
                                  </button>
                                  {/* KSD3 — Include / exclude this doc from SED extraction.
                                       Default (`includeInSed` undefined) is "in SED"; explicit
                                       false flips to excluded. The bootstrap pipeline reads
                                       this flag and skips excluded docs entirely. Composes
                                       with KSD4's Document Contents panel (mirrors there too)
                                       and with the UD pack (preserved across rebuilds). */}
                                  {isSiteMode && (
                                    <button
                                      onClick={async () => {
                                        const before = doc.includeInSed !== false;
                                        const nextValue = !before; // flip user-visible "In SED?" boolean
                                        // DDL-3 — cross-array field write.
                                        try {
                                          await commitFieldUpdate(doc.id, { includeInSed: nextValue });
                                          toast.success(nextValue
                                            ? `${doc.fileName} included in SED extraction`
                                            : `${doc.fileName} excluded from SED extraction`,
                                          );
                                        } catch (error) {
                                          toast.error(`SED inclusion was not saved: ${(error as Error).message}`);
                                        }
                                      }}
                                      className={`p-1.5 type-body uppercase tracking-widest transition-colors ${
                                        doc.includeInSed === false
                                          ? 'text-danger hover:text-danger/70'
                                          : 'text-text-secondary hover:text-primary'
                                      }`}
                                      title={doc.includeInSed === false
                                        ? 'Excluded from SED extraction — click to include'
                                        : 'Contributing to SED — click to exclude (useful for duplicates or irrelevant docs)'}
                                      data-testid={`doc-include-in-sed-${doc.id}`}
                                    >
                                      {doc.includeInSed === false ? '⊘' : '✓'}
                                    </button>
                                  )}
                                  {/* Include in Report toggle for images */}
                                  {ext.match(/^(jpg|jpeg|png|gif|bmp|svg)$/) && (
                                    <button
                                      onClick={async () => {
                                        // DDL-3 — cross-array field write.
                                        try {
                                          await commitFieldUpdate(doc.id, { includeInReport: !doc.includeInReport });
                                          toast.success(doc.includeInReport ? 'Removed from report' : 'Will include in report');
                                        } catch (error) {
                                          toast.error(`Report inclusion was not saved: ${(error as Error).message}`);
                                        }
                                      }}
                                      className={`p-1.5 text-sm transition-colors ${doc.includeInReport ? 'text-info' : 'text-text-secondary hover:text-info'}`}
                                      title={doc.includeInReport ? 'Included in report — click to remove' : 'Include this image in the report'}
                                    >
                                      {doc.includeInReport ? '📎' : '📌'}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => { setEditingId(doc.id); setEditName(doc.fileName); }}
                                    className="p-1.5 text-text-secondary hover:text-primary transition-colors"
                                    title="Rename"
                                    data-testid={`doc-rename-${doc.id}`}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  {/* Download — disabled for legacy docs uploaded before
                                       rawStoragePath persistence. */}
                                  <button
                                    onClick={async () => {
                                      try {
                                        const { downloadDocument } = await import('../../lib/documentDownload');
                                        await downloadDocument(doc, binaryLoader);
                                      } catch (e) {
                                        toast.error((e as Error).message || 'Download failed');
                                      }
                                    }}
                                    className="p-1.5 text-text-secondary hover:text-primary transition-colors disabled:opacity-30 disabled:hover:text-text-secondary disabled:cursor-not-allowed"
                                    title={doc.rawStoragePath ? 'Download' : 'Re-upload to enable download'}
                                    data-testid={`doc-download-${doc.id}`}
                                    disabled={!doc.rawStoragePath}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  {/* DMF-1 — Move to folder. Opens MoveFolderDialog (modal),
                                       replacing the legacy inline popover that fell through
                                       to the row-level click handler under some mount paths
                                       (clicking the icon navigated to ?tab=evidence instead
                                       of opening a picker). The dialog renders once for the
                                       whole library — see bottom of this component. */}
                                  <button
                                    onClick={() => setMoveDialogDocId(doc.id)}
                                    className="p-1.5 text-text-secondary hover:text-primary transition-colors"
                                    title="Move to folder"
                                    data-testid={`doc-move-${doc.id}`}
                                  >
                                    <FolderOpen className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(doc.id)}
                                    className="p-1.5 text-text-secondary hover:text-danger transition-colors"
                                    title="Delete"
                                    data-testid={`doc-delete-${doc.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                  {/* SET-B1 — Documents → Site Evidence bridge.
                                       Same affordance as the cards view; hidden
                                       when the host doesn't provide the
                                       handler (legacy / report-only mounts). */}
                                  {onJumpToEvidence && (
                                    <button
                                      onClick={() => onJumpToEvidence(doc.id)}
                                      className="p-1.5 text-text-secondary hover:text-primary transition-colors"
                                      title="View SED contributions for this document"
                                      data-testid={`doc-jump-evidence-${doc.id}`}
                                      aria-label={`View SED contributions for ${doc.fileName}`}
                                    >
                                      <Sparkles className="w-4 h-4" />
                                    </button>
                                  )}
                                  {/* Detail drawer trigger — surfaces summary,
                                      tags, relevance heatmap, used-in. */}
                                  <button
                                    onClick={() => setDetailDocId(doc.id)}
                                    className="p-1.5 text-text-secondary hover:text-primary transition-colors"
                                    title="Show details"
                                    data-testid={`doc-detail-${doc.id}`}
                                  >
                                    <Info className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — hidden in embedded mode */}
        {!embedded && (
          <div className="px-4 py-3 border-t border-border flex justify-end shrink-0">
            <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
        {/* Inline document preview — single dialog used by every row.
             Lazy-renders the right viewer (PDF / DOCX / XLSX / image
             / video / text / fallback) by mime + extension. DAT-2/3 —
             propagates `initialPage` so "Open at page" / "Verify"
             buttons in the lineage inspector land on the right page. */}
        <DocViewerDialog
          binaryContext={binaryContext}
          bodyLoader={bodyLoader}
          doc={viewerDoc}
          open={!!viewerDoc}
          onClose={() => { setViewerDoc(null); setViewerInitialPage(undefined); }}
          initialPage={viewerInitialPage}
        />
        {/* DAT-2/3 — doc-first lineage inspector. Opens from the
             DocApplicabilityChips "Show detail" button. Lazy-loads the
             site's SED on open. The Open-at-page / Verify buttons
             forward into the same DocViewerDialog above by stashing
             the page in `viewerInitialPage`. */}
        <DocLineageInspector
          doc={lineageDoc}
          site={usageContext.site}
          open={!!lineageDoc}
          onClose={() => setLineageDocId(null)}
          onOpenAtPage={(d, page) => {
            setViewerInitialPage(typeof page === 'number' ? page : undefined);
            setViewerDoc(d);
          }}
          onViewVariable={onJumpToVariable
            ? (sectionId, fieldKey) => {
                // DF-3 — close the inspector first so the user lands
                // on the Site Evidence panel cleanly, then bubble
                // the deep-link to the parent (SiteLandingPage) which
                // sets `?focus=variable:<sectionId>.<fieldKey>` and
                // flips the active tab.
                setLineageDocId(null);
                onJumpToVariable(sectionId, fieldKey);
              }
            : undefined}
        />
        {/* Doc detail drawer — slides in from the right with the
             selected doc's summary / tags / relevance / used-in. */}
        <DocDetailDrawer
          bodyLoader={bodyLoader}
          doc={detailDoc}
          onClose={() => setDetailDocId(null)}
          onChange={handleDetailDocChange}
          reports={usageContext.reports}
          site={usageContext.site}
          onPreview={(d) => { setViewerDoc(d); setDetailDocId(null); }}
        />
        {/* DMF-1 — Move-to-folder modal. Mounted once for the whole
             library; the per-row Move button sets moveDialogDocId.
             Rendered only when (a) we have a siteId (site-mode — the
             only mode whose persistence is wired through the cross-array
             moveSiteDocument action) and (b) the targeted doc still
             exists in `documents` (handles concurrent delete races). */}
        {siteId && moveDialogDocId && (() => {
          const targetDoc = documents.find(d => d.id === moveDialogDocId);
          if (!targetDoc) return null;
          // Existing folder names + user-created empty buckets, deduped.
          // Excludes the auto-categorizer fallback bucket name for the
          // current doc only if it's identical to the doc's folder
          // override; we keep "General" et al. visible so users can
          // explicitly route there.
          const folderNames = Array.from(
            new Set([
              ...folders.map(([name]) => name),
              ...customFolders,
            ]),
          ).sort((a, b) => {
            if (a === 'General') return 1;
            if (b === 'General') return -1;
            return a.localeCompare(b);
          });
          return (
            <MoveFolderDialog
              siteId={siteId}
              doc={targetDoc}
              folders={folderNames}
              open
              onClose={() => setMoveDialogDocId(null)}
              onMoved={() => {
                // Publish the checked canonical folder metadata; rebuilding
                // the derived library map is a separate explicit workflow.
                onDocumentsChange?.(freshDocs());
              }}
            />
          );
        })()}
    </>
  );

  return embedded ? (
    <DocLibraryEmbeddedShell>{body}</DocLibraryEmbeddedShell>
  ) : (
    <DocLibraryModalShell onClose={onClose}>{body}</DocLibraryModalShell>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * DocumentLibraryEmptyState — Documents_On_Site Prompt 8-C.
 *
 * Rich first-time empty state. Renders inside the embedded library
 * tab when the site has no docs and triggers the existing hidden
 * file inputs via [data-doc-library-upload-*] selectors so we don't
 * duplicate the upload pipeline.
 * ───────────────────────────────────────────────────────────────── */
const DocumentLibraryEmptyState: React.FC<{
  embedded: boolean;
  recommendedReportTypes?: ReportTypeId[];
  onUpload: (folder: boolean) => void;
  onSkip?: () => void;
}> = ({ embedded, recommendedReportTypes, onUpload, onSkip }) => {
  const checklist = useMemo(() => {
    if (!recommendedReportTypes || recommendedReportTypes.length === 0) return [];
    return getDocumentChecklistAcross(recommendedReportTypes).slice(0, 5);
  }, [recommendedReportTypes]);

  const siteTypeLabel = useMemo(() => {
    if (!recommendedReportTypes || recommendedReportTypes.length === 0) return null;
    if (recommendedReportTypes.length === 1) {
      return REPORT_TYPE_REGISTRY[recommendedReportTypes[0]!]?.shortLabel?.toLowerCase() ?? null;
    }
    return 'these';
  }, [recommendedReportTypes]);

  // The bare-modal mode keeps the existing terse copy — the rich state
  // is the embedded site-mode that the SiteLandingPage Documents tab
  // mounts. Modal users already had a wizard-flow leading them here.
  if (!embedded) {
    return (
      <div className="text-center py-8 text-[10px] text-text-secondary uppercase tracking-widest">
        No documents uploaded yet
      </div>
    );
  }

  return (
    <div className="py-8 px-6 text-center space-y-5" data-testid="doc-library-empty-state">
      <div className="space-y-1">
        <div className="text-3xl">📄</div>
        <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">
          Add evidence to this site
        </h3>
      </div>
      <div className="text-[11px] text-text-secondary max-w-md mx-auto leading-relaxed">
        Reports are stronger when grounded in your documents. Drop a folder, click upload,
        or skip — you can always add documents later.
      </div>
      {checklist.length > 0 && (
        <div className="max-w-md mx-auto text-left rounded-md border border-border bg-state-hover p-3 space-y-1.5">
          <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
            Recommended for {siteTypeLabel ?? 'these'} sites
          </div>
          <ul className="space-y-1 text-[10px] text-text-primary">
            {checklist.map(entry => (
              <li key={entry.id} className="flex gap-1.5">
                <span className="text-text-muted">•</span>
                <span>
                  {entry.label}
                  {entry.description ? <span className="text-text-muted"> — {entry.description}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Button
          size="sm"
          className="h-8 type-body uppercase tracking-widest"
          onClick={() => onUpload(false)}
        >
          <Upload className="w-3 h-3 mr-1" /> Upload files
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 type-body uppercase tracking-widest"
          onClick={() => onUpload(true)}
        >
          <FolderOpen className="w-3 h-3 mr-1" /> Upload folder
        </Button>
        {onSkip && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 type-body uppercase tracking-widest text-text-muted"
            onClick={onSkip}
          >
            Skip — generate from public data
          </Button>
        )}
      </div>
    </div>
  );
};
